import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, DollarSign, TrendingUp, TrendingDown, Users, 
  Calendar, AlertCircle, CheckCircle, Clock, Home,
  BarChart3, PieChart, ArrowUp, ArrowDown, Search, Bell, CheckSquare, User, X,
  MessageCircle, Mail, Sun, Send, Bot, Loader2, Trash2, Maximize2, Save, Vault, Paperclip, Smile, Mic, Volume2,
  Play, Eye, Film, Phone, UserCircle, Plane, MapPin, Video, CalendarClock, FileText, Plus, Upload
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';
import { apiCall } from '../services/api';

// API URL for media storage
const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Parse time string to minutes for sorting (handles various formats)
// Moved outside component to avoid recreation on each render
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || timeStr === 'missing value' || timeStr === '-' || timeStr === 'N/A') return 9999;
  
  const str = String(timeStr).trim();
  const isPM = str.toLowerCase().includes('pm');
  const isAM = str.toLowerCase().includes('am');
  const cleanTime = str.replace(/[ap]m/gi, '').trim();
  
  const parts = cleanTime.split(':');
  if (parts.length >= 2) {
    let hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) return 9999;
    
    // Convert 12-hour to 24-hour if needed
    if (isPM && hours < 12) hours += 12;
    if (isAM && hours === 12) hours = 0;
    
    return hours * 60 + minutes;
  }
  return 9999;
};

const RealEstateManagingPartnerDashboardNew = () => {
  const navigate = useNavigate();
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [messengerTab, setMessengerTab] = useState('text'); // For quick messenger tabs
  const [brightness, setBrightness] = useState(100); // Brightness level - starts at max
  const [volume, setVolume] = useState(45); // Volume level
  const [currentTime, setCurrentTime] = useState(new Date()); // Real-time clock
  const [showMusicPlayer, setShowMusicPlayer] = useState(false); // Bottom music player
  const [isPlaying, setIsPlaying] = useState(false); // Music playing state
  const [calendarDate, setCalendarDate] = useState(new Date()); // Calendar month navigation
  
  // Spotify state
  const [spotifyConnected, setSpotifyConnected] = useState(() => {
    return !!localStorage.getItem('spotify_access_token');
  });
  const [spotifyToken, setSpotifyToken] = useState(() => localStorage.getItem('spotify_access_token'));
  const [currentTrack, setCurrentTrack] = useState(null);
  const [spotifyLoading, setSpotifyLoading] = useState(false);
  const [spotifyVolume, setSpotifyVolume] = useState(50);
  const [spotifyShuffle, setSpotifyShuffle] = useState(false);
  const [spotifyRepeat, setSpotifyRepeat] = useState('off'); // 'off', 'context', 'track'
  const [spotifyDevices, setSpotifyDevices] = useState([]);
  const [activeDevice, setActiveDevice] = useState(null);
  const [showDevicesDropdown, setShowDevicesDropdown] = useState(false);
  const [spotifyProgress, setSpotifyProgress] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [spotifyArtistInfo, setSpotifyArtistInfo] = useState(null);
  const [spotifyTrackDetails, setSpotifyTrackDetails] = useState(null);
  const [spotifyModalTab, setSpotifyModalTab] = useState('player'); // 'player', 'artist', 'album'
  const [spotifyLoadingDetails, setSpotifyLoadingDetails] = useState(false);
  const currentTrackIdRef = useRef(null); // Track ID ref to avoid stale closure issues
  const fetchingArtistIdRef = useRef(null); // Prevent duplicate fetches
  const fetchingTrackIdRef = useRef(null); // Prevent duplicate fetches
  
  // Modal states
  const [activeModal, setActiveModal] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  
  // Profile state
  const [profileImage, setProfileImage] = useState(() => {
    return localStorage.getItem('dashboard_profile_image') || 'https://customer-assets.emergentagent.com/job_barber-dash-3/artifacts/rperhrt8_1583282364DW.jpg';
  });
  const [profileName, setProfileName] = useState(() => {
    return localStorage.getItem('dashboard_profile_name') || 'Darren Woodson';
  });
  const [profileRole, setProfileRole] = useState(() => {
    return localStorage.getItem('dashboard_profile_role') || 'Managing Partner';
  });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [tempProfileImage, setTempProfileImage] = useState('');
  const [tempProfileName, setTempProfileName] = useState('');
  const [tempProfileRole, setTempProfileRole] = useState('');
  const profileFileInputRef = useRef(null);
  
  // Weather state
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLocation, setWeatherLocation] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState(null);
  
  // Calculator state
  const [calcDisplay, setCalcDisplay] = useState('0');
  const [calcPrevValue, setCalcPrevValue] = useState(null);
  const [calcOperator, setCalcOperator] = useState(null);
  const [calcWaitingForOperand, setCalcWaitingForOperand] = useState(false);
  
  // Notes state
  const [currentNote, setCurrentNote] = useState('');
  const [savedNotes, setSavedNotes] = useState(() => {
    const saved = localStorage.getItem('dashboard_notes');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Screen Recording state
  const [isScreenRecording, setIsScreenRecording] = useState(false);
  const [screenRecordingTime, setScreenRecordingTime] = useState(0);
  const [screenRecordings, setScreenRecordings] = useState(() => {
    const saved = localStorage.getItem('dashboard_screen_recordings');
    return saved ? JSON.parse(saved) : [];
  });
  const screenMediaRecorderRef = useRef(null);
  const screenChunksRef = useRef([]);
  const screenStreamRef = useRef(null);
  
  // Alarms state
  const [alarms, setAlarms] = useState(() => {
    const saved = localStorage.getItem('dashboard_alarms');
    return saved ? JSON.parse(saved) : [
      { id: 1, time: '7:00 AM', label: 'Wake Up', enabled: true },
      { id: 2, time: '2:30 PM', label: 'Site Visit', enabled: true }
    ];
  });
  const [newAlarmTime, setNewAlarmTime] = useState('09:00');
  const [newAlarmLabel, setNewAlarmLabel] = useState('');
  
  // Voice Recorder state
  const [voiceRecordings, setVoiceRecordings] = useState(() => {
    const saved = localStorage.getItem('dashboard_recordings');
    return saved ? JSON.parse(saved) : [];
  });
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [voiceMediaRecorder, setVoiceMediaRecorder] = useState(null);
  const recordingIntervalRef = useRef(null);
  
  // Calculator functions
  const calcInputDigit = (digit) => {
    if (calcWaitingForOperand) {
      setCalcDisplay(digit);
      setCalcWaitingForOperand(false);
    } else {
      setCalcDisplay(calcDisplay === '0' ? digit : calcDisplay + digit);
    }
  };
  
  const calcInputDecimal = () => {
    if (calcWaitingForOperand) {
      setCalcDisplay('0.');
      setCalcWaitingForOperand(false);
    } else if (!calcDisplay.includes('.')) {
      setCalcDisplay(calcDisplay + '.');
    }
  };
  
  const calcClear = () => {
    setCalcDisplay('0');
    setCalcPrevValue(null);
    setCalcOperator(null);
    setCalcWaitingForOperand(false);
  };
  
  const calcPerformOperation = (nextOperator) => {
    const inputValue = parseFloat(calcDisplay);
    
    if (calcPrevValue === null) {
      setCalcPrevValue(inputValue);
    } else if (calcOperator) {
      const currentValue = calcPrevValue || 0;
      let result;
      switch (calcOperator) {
        case '+': result = currentValue + inputValue; break;
        case '-': result = currentValue - inputValue; break;
        case '×': result = currentValue * inputValue; break;
        case '÷': result = inputValue !== 0 ? currentValue / inputValue : 'Error'; break;
        default: result = inputValue;
      }
      setCalcDisplay(String(result));
      setCalcPrevValue(result);
    }
    
    setCalcWaitingForOperand(true);
    setCalcOperator(nextOperator);
  };
  
  const calcEquals = () => {
    if (!calcOperator || calcPrevValue === null) return;
    calcPerformOperation(null);
    setCalcOperator(null);
  };
  
  // Profile functions
  const openProfileModal = () => {
    setTempProfileImage(profileImage);
    setTempProfileName(profileName);
    setTempProfileRole(profileRole);
    setShowProfileModal(true);
  };
  
  const saveProfileChanges = () => {
    let nameChanged = false;
    const newName = tempProfileName;
    
    if (tempProfileImage) {
      setProfileImage(tempProfileImage);
      localStorage.setItem('dashboard_profile_image', tempProfileImage);
    }
    if (tempProfileName && tempProfileName !== profileName) {
      setProfileName(tempProfileName);
      localStorage.setItem('dashboard_profile_name', tempProfileName);
      nameChanged = true;
    }
    if (tempProfileRole) {
      setProfileRole(tempProfileRole);
      localStorage.setItem('dashboard_profile_role', tempProfileRole);
    }
    setShowProfileModal(false);
    
    // If name changed, regenerate the daily brief welcome message
    if (nameChanged && newName) {
      const firstName = newName.split(' ')[0];
      
      // Update the first message (daily brief) with the new name
      setAiMessages(prev => {
        if (prev.length > 0 && prev[0].role === 'assistant') {
          const updatedMessages = [...prev];
          // Replace the welcome message with the new name
          const updatedContent = updatedMessages[0].content.replace(
            /Welcome back, \w+!/,
            `Welcome back, ${firstName}!`
          );
          updatedMessages[0] = { ...updatedMessages[0], content: updatedContent };
          localStorage.setItem('ai_current_conversation', JSON.stringify(updatedMessages));
          return updatedMessages;
        }
        return prev;
      });
    }
  };
  
  const handleProfileImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Notes functions
  const saveNote = () => {
    if (currentNote.trim()) {
      const newNote = {
        id: Date.now(),
        content: currentNote,
        title: currentNote.split('\n')[0].substring(0, 50) || 'Untitled Note',
        updatedAt: new Date().toISOString()
      };
      const updatedNotes = [newNote, ...savedNotes];
      setSavedNotes(updatedNotes);
      localStorage.setItem('dashboard_notes', JSON.stringify(updatedNotes));
      setCurrentNote('');
    }
  };
  
  const loadNote = (note) => {
    setCurrentNote(note.content);
  };
  
  const deleteNote = (noteId) => {
    const updatedNotes = savedNotes.filter(n => n.id !== noteId);
    setSavedNotes(updatedNotes);
    localStorage.setItem('dashboard_notes', JSON.stringify(updatedNotes));
  };

  // Screen Recording functions
  const startScreenRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { 
          mediaSource: 'screen',
          cursor: 'always'
        },
        audio: true
      });
      
      screenStreamRef.current = stream;
      screenChunksRef.current = [];
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9'
      });
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          screenChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(screenChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        
        // Auto-download the recording
        const a = document.createElement('a');
        a.href = url;
        a.download = `screen-recording-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Save recording info to list
        const newRecording = {
          id: Date.now(),
          name: `Recording ${screenRecordings.length + 1}`,
          duration: screenRecordingTime,
          date: new Date().toISOString(),
          url: url
        };
        const updatedRecordings = [newRecording, ...screenRecordings];
        setScreenRecordings(updatedRecordings);
        localStorage.setItem('dashboard_screen_recordings', JSON.stringify(updatedRecordings.map(r => ({...r, url: null}))));
        
        setIsScreenRecording(false);
        setScreenRecordingTime(0);
      };
      
      // Handle when user stops sharing via browser UI
      stream.getVideoTracks()[0].onended = () => {
        if (screenMediaRecorderRef.current && screenMediaRecorderRef.current.state !== 'inactive') {
          stopScreenRecording();
        }
      };
      
      screenMediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setIsScreenRecording(true);
      
    } catch (error) {
      console.error('Screen recording error:', error);
      if (error.name !== 'NotAllowedError') {
        alert('Failed to start screen recording. Please make sure you have granted screen sharing permissions.');
      }
    }
  };
  
  const stopScreenRecording = () => {
    if (screenMediaRecorderRef.current && screenMediaRecorderRef.current.state !== 'inactive') {
      screenMediaRecorderRef.current.stop();
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
  };
  
  // Screen recording timer
  useEffect(() => {
    let interval;
    if (isScreenRecording) {
      interval = setInterval(() => {
        setScreenRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isScreenRecording]);
  
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Alarm functions
  const toggleAlarm = (alarmId) => {
    const updatedAlarms = alarms.map(a => 
      a.id === alarmId ? { ...a, enabled: !a.enabled } : a
    );
    setAlarms(updatedAlarms);
    localStorage.setItem('dashboard_alarms', JSON.stringify(updatedAlarms));
  };
  
  const addAlarm = () => {
    if (newAlarmTime) {
      const [hours, minutes] = newAlarmTime.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const timeStr = `${displayHour}:${minutes} ${ampm}`;
      
      const newAlarm = {
        id: Date.now(),
        time: timeStr,
        label: newAlarmLabel || 'Alarm',
        enabled: true
      };
      const updatedAlarms = [...alarms, newAlarm];
      setAlarms(updatedAlarms);
      localStorage.setItem('dashboard_alarms', JSON.stringify(updatedAlarms));
      setNewAlarmTime('09:00');
      setNewAlarmLabel('');
    }
  };
  
  const deleteAlarm = (alarmId) => {
    const updatedAlarms = alarms.filter(a => a.id !== alarmId);
    setAlarms(updatedAlarms);
    localStorage.setItem('dashboard_alarms', JSON.stringify(updatedAlarms));
  };
  
  // Voice Recorder functions
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];
      
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        const newRecording = {
          id: Date.now(),
          name: `Recording ${voiceRecordings.length + 1}`,
          duration: formatRecordingTime(recordingTime),
          date: 'Just now',
          url: url
        };
        const updatedRecordings = [newRecording, ...voiceRecordings];
        setVoiceRecordings(updatedRecordings);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setVoiceMediaRecorder(mediaRecorder);
      setIsRecordingVoice(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };
  
  const stopVoiceRecording = () => {
    if (voiceMediaRecorder) {
      voiceMediaRecorder.stop();
      setVoiceMediaRecorder(null);
    }
    setIsRecordingVoice(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
  };
  
  const playRecording = (recording) => {
    const audio = new Audio(recording.url);
    audio.play();
  };
  
  const deleteRecording = (recordingId) => {
    const updatedRecordings = voiceRecordings.filter(r => r.id !== recordingId);
    setVoiceRecordings(updatedRecordings);
  };
  
  // Today's Outlook calendar events with completion status
  const [todayOutlookEvents, setTodayOutlookEvents] = useState([]);
  const [outlookEventsLoading, setOutlookEventsLoading] = useState(true);
  const [completedEventIds, setCompletedEventIds] = useState(() => {
    // Load completed events from localStorage
    const saved = localStorage.getItem('completed_calendar_events');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Calendar events for the month (for showing dots on calendar)
  const [monthCalendarEvents, setMonthCalendarEvents] = useState([]);
  
  // Event category colors
  const eventCategoryColors = {
    birthday: 'bg-pink-500',
    health: 'bg-red-500',
    sports: 'bg-green-500',
    travel: 'bg-blue-500',
    home: 'bg-yellow-500',
    social: 'bg-purple-500',
    education: 'bg-indigo-500',
    networking: 'bg-cyan-500',
    meeting: 'bg-orange-500',
    default: 'bg-gray-500'
  };
  
  // Get event category from subject/title
  const getEventCategory = (subject) => {
    if (!subject) return 'default';
    const lower = subject.toLowerCase();
    if (lower.includes('birthday') || lower.includes('bday')) return 'birthday';
    if (lower.includes('health') || lower.includes('doctor') || lower.includes('medical') || lower.includes('gym') || lower.includes('workout')) return 'health';
    if (lower.includes('sport') || lower.includes('game') || lower.includes('match') || lower.includes('football') || lower.includes('basketball')) return 'sports';
    if (lower.includes('travel') || lower.includes('flight') || lower.includes('trip') || lower.includes('vacation')) return 'travel';
    if (lower.includes('home') || lower.includes('house') || lower.includes('maintenance') || lower.includes('repair')) return 'home';
    if (lower.includes('social') || lower.includes('party') || lower.includes('dinner') || lower.includes('lunch') || lower.includes('drinks')) return 'social';
    if (lower.includes('education') || lower.includes('class') || lower.includes('course') || lower.includes('training') || lower.includes('learn')) return 'education';
    if (lower.includes('network') || lower.includes('conference') || lower.includes('meetup') || lower.includes('event')) return 'networking';
    if (lower.includes('meeting') || lower.includes('call') || lower.includes('zoom')) return 'meeting';
    return 'default';
  };
  
  // Check if an event time has passed (uses currentTime state for reactivity)
  const isEventPast = (eventTime) => {
    if (!eventTime || eventTime === 'missing value' || eventTime === '-' || eventTime === 'N/A') return false;
    const now = currentTime; // Use state variable for reactivity
    
    // Parse time - handle various formats like "9:30", "09:30", "9:30 AM", etc.
    let hours = 0, minutes = 0;
    const timeStr = String(eventTime).trim();
    
    // Check for AM/PM format
    const isPM = timeStr.toLowerCase().includes('pm');
    const isAM = timeStr.toLowerCase().includes('am');
    const cleanTime = timeStr.replace(/[ap]m/gi, '').trim();
    
    const parts = cleanTime.split(':');
    if (parts.length >= 2) {
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
      
      // Convert 12-hour to 24-hour if needed
      if (isPM && hours < 12) hours += 12;
      if (isAM && hours === 12) hours = 0;
    }
    
    if (isNaN(hours) || isNaN(minutes)) return false;
    
    const eventDate = new Date(now);
    eventDate.setHours(hours, minutes, 0, 0);
    return now > eventDate;
  };
  
  // Toggle event completion manually
  const toggleEventComplete = (eventId) => {
    setCompletedEventIds(prev => {
      const newCompleted = prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId];
      localStorage.setItem('completed_calendar_events', JSON.stringify(newCompleted));
      return newCompleted;
    });
  };
  
  // Fetch today's Outlook events
  useEffect(() => {
    const fetchTodayOutlookEvents = async () => {
      try {
        // Use local date, not UTC - this ensures we show events for the user's actual "today"
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const todayStr = `${year}-${month}-${day}`; // Local date in YYYY-MM-DD format
        
        console.log(`📅 Fetching events for local date: ${todayStr}`);
        
        const response = await fetch(`${API_URL}/api/outlook-calendar/desktop-events?start_date=${todayStr}&end_date=${todayStr}`);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success' && Array.isArray(data.events)) {
            // Filter out invalid events and clean up "missing value" text
            const cleanedEvents = data.events
              .map(event => ({
                ...event,
                // Clean up subject - remove or replace "missing value"
                subject: (event.subject && event.subject !== 'missing value') ? event.subject : (event.title || 'Untitled Event'),
                // Clean up time - handle "missing value"
                time: (event.time && event.time !== 'missing value') ? event.time : null,
                // Clean up location - handle "missing value"
                location: (event.location && event.location !== 'missing value') ? event.location : null
              }))
              .filter(event => event.subject && event.subject !== 'missing value');
            
            // Deduplicate events by subject + time combination
            const uniqueEventsMap = new Map();
            cleanedEvents.forEach(event => {
              const key = `${event.subject || ''}-${event.time || ''}-${event.date || ''}`;
              if (!uniqueEventsMap.has(key)) {
                uniqueEventsMap.set(key, event);
              }
            });
            const uniqueEvents = Array.from(uniqueEventsMap.values());
            
            // Sort events by time (earliest first) using proper time parsing
            const sortedEvents = uniqueEvents.sort((a, b) => {
              const timeA = parseTimeToMinutes(a.time);
              const timeB = parseTimeToMinutes(b.time);
              return timeA - timeB;
            });
            
            setTodayOutlookEvents(sortedEvents);
            console.log(`📅 Loaded ${sortedEvents.length} unique Outlook events for today (${todayStr})`);
          }
        }
      } catch (error) {
        console.error('Error fetching Outlook events:', error);
      } finally {
        setOutlookEventsLoading(false);
      }
    };
    
    fetchTodayOutlookEvents();
    // Refresh every 5 minutes
    const interval = setInterval(fetchTodayOutlookEvents, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Clear completed events at midnight (new day)
  useEffect(() => {
    const checkMidnight = () => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 0) {
        setCompletedEventIds([]);
        localStorage.removeItem('completed_calendar_events');
      }
    };
    const midnightCheck = setInterval(checkMidnight, 60000);
    return () => clearInterval(midnightCheck);
  }, []);

  // Fetch calendar events for the displayed month
  useEffect(() => {
    const fetchMonthEvents = async () => {
      try {
        const year = calendarDate.getFullYear();
        const month = calendarDate.getMonth();
        const startDate = new Date(year, month, 1).toISOString().split('T')[0];
        const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
        
        const response = await fetch(`${API_URL}/api/outlook-calendar/desktop-events?start_date=${startDate}&end_date=${endDate}`);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success' && Array.isArray(data.events)) {
            setMonthCalendarEvents(data.events);
          }
        }
      } catch (error) {
        console.log('Could not fetch month events');
      }
    };
    fetchMonthEvents();
  }, [calendarDate]);

  // Get events for a specific day
  const getEventsForDay = (day) => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return monthCalendarEvents.filter(event => {
      const eventDate = event.start?.dateTime?.split('T')[0] || event.date;
      return eventDate === dateStr;
    });
  };

  // Latest Content state
  const [latestVideos, setLatestVideos] = useState([]);
  const [latestVideosLoading, setLatestVideosLoading] = useState(true);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Fetch latest videos
  useEffect(() => {
    const fetchLatestVideos = async () => {
      try {
        const response = await fetch(`${API_URL}/api/media-storage/videos`);
        if (response.ok) {
          const data = await response.json();
          // Get the 5 most recent videos
          setLatestVideos((data.videos || []).slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching latest videos:', error);
      } finally {
        setLatestVideosLoading(false);
      }
    };
    fetchLatestVideos();
  }, []);

  // Navigate to next/previous video
  const nextVideo = () => {
    setCurrentVideoIndex(prev => (prev + 1) % latestVideos.length);
  };
  const prevVideo = () => {
    setCurrentVideoIndex(prev => (prev - 1 + latestVideos.length) % latestVideos.length);
  };

  // Get thumbnail URL helper
  const getVideoThumbnail = (video) => {
    if (video.thumbnail) {
      if (video.thumbnail.startsWith('/api/')) {
        return `${API_URL}${video.thumbnail}`;
      }
      return video.thumbnail;
    }
    return 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop';
  };

  // Recent Documents state
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDashboardMeeting, setSelectedDashboardMeeting] = useState(null);
  const [selectedDashboardContact, setSelectedDashboardContact] = useState(null);
  const [documentUploading, setDocumentUploading] = useState(false);
  const documentFileInputRef = useRef(null);

  // Handle document upload from dashboard
  const handleDashboardDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setDocumentUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('category', 'Properties');
      
      const response = await fetch(`${API_URL}/api/documents-storage/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const newDoc = await response.json();
        // Add new document to the beginning of the list
        setRecentDocuments(prev => [newDoc, ...prev].slice(0, 4));
        // Reset file input
        if (documentFileInputRef.current) {
          documentFileInputRef.current.value = '';
        }
      } else {
        console.error('Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setDocumentUploading(false);
    }
  };

  // Fetch recent documents
  useEffect(() => {
    const fetchRecentDocuments = async () => {
      try {
        const response = await fetch(`${API_URL}/api/documents-storage/documents`);
        if (response.ok) {
          const data = await response.json();
          // Get the 4 most recent documents (4 visible before scroll)
          setRecentDocuments((data.documents || []).slice(0, 4));
        }
      } catch (error) {
        console.error('Error fetching recent documents:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };
    fetchRecentDocuments();
  }, []);

  // Get file type icon helper
  const getFileIcon = (fileType) => {
    const type = (fileType || '').toUpperCase();
    if (type.includes('PDF')) return { icon: '📄', color: 'text-red-400' };
    if (type.includes('DOC') || type.includes('WORD')) return { icon: '📝', color: 'text-white' };
    if (type.includes('XLS') || type.includes('EXCEL') || type.includes('SHEET')) return { icon: '📊', color: 'text-green-400' };
    if (type.includes('PPT') || type.includes('PRESENTATION')) return { icon: '📽️', color: 'text-orange-400' };
    if (type.includes('IMG') || type.includes('IMAGE') || type.includes('PNG') || type.includes('JPG') || type.includes('JPEG')) return { icon: '🖼️', color: 'text-purple-400' };
    if (type.includes('ZIP') || type.includes('RAR')) return { icon: '📦', color: 'text-yellow-400' };
    return { icon: '📁', color: 'text-gray-400' };
  };

  // Format inline markdown (bold, italic, code, links)
  const formatInlineMarkdown = (text) => {
    if (!text) return '';
    return text
      // Bold: **text** or __text__
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
      .replace(/__(.+?)__/g, '<strong class="font-semibold text-white">$1</strong>')
      // Italic: *text* or _text_
      .replace(/\*([^*]+)\*/g, '<em class="italic text-gray-200">$1</em>')
      .replace(/_([^_]+)_/g, '<em class="italic text-gray-200">$1</em>')
      // Inline code: `code`
      .replace(/`([^`]+)`/g, '<code class="bg-gray-700 px-1.5 py-0.5 rounded text-white text-xs font-mono">$1</code>')
      // Links: [text](url)
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="text-white hover:text-white underline">$1</a>');
  };

  // Recent Contacts state
  const [recentContacts, setRecentContacts] = useState([]);
  const [recentContactsLoading, setRecentContactsLoading] = useState(true);

  // Auto-categorize iCloud contacts (same logic as contacts page)
  const categorizeContact = (contact) => {
    const searchText = `${contact.organization || ''} ${contact.emails?.map(e => e.email).join(' ') || ''} ${contact.notes || ''} ${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase();
    
    if (searchText.includes('password') || searchText.includes('login') || searchText.includes('credential')) return 'Passwords';
    if (searchText.includes('realtor') || searchText.includes('real estate') || searchText.includes('broker') || searchText.includes('property')) return 'Real Estate';
    if (searchText.includes('health') || searchText.includes('medical') || searchText.includes('doctor')) return 'Healthcare';
    if (searchText.includes('tech') || searchText.includes('software') || searchText.includes('developer')) return 'Technology';
    if (searchText.includes('financ') || searchText.includes('bank') || searchText.includes('invest') || searchText.includes('cpa')) return 'Finance';
    if (searchText.includes('sport') || searchText.includes('athlete') || searchText.includes('nfl') || searchText.includes('cowboy')) return 'Sports';
    if (searchText.includes('law') || searchText.includes('attorney') || searchText.includes('legal')) return 'Legal';
    if (searchText.includes('family') || searchText.includes('wife') || searchText.includes('husband')) return 'Family';
    if (!contact.organization || contact.organization.trim() === '') return 'Friends';
    return 'Other';
  };

  // Fetch recent contacts from localStorage (same source as contacts page)
  useEffect(() => {
    const fetchRecentContacts = () => {
      try {
        const storedContacts = localStorage.getItem('icloud_contacts');
        if (storedContacts) {
          const contacts = JSON.parse(storedContacts);
          // Process and format contacts for display
          const processed = contacts
            .filter(c => {
              // Filter out contacts with no name
              const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
              return name && name !== 'Unknown';
            })
            .map(c => ({
              id: c.id,
              name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
              email: c.emails?.[0]?.email || '',
              phone: c.phones?.[0]?.phone || '',
              company: c.organization || '',
              category: categorizeContact(c),
              raw: c
            }))
            .slice(0, 4); // Get 4 most recent
          setRecentContacts(processed);
        }
      } catch (error) {
        console.error('Error fetching recent contacts:', error);
      } finally {
        setRecentContactsLoading(false);
      }
    };
    fetchRecentContacts();
    
    // Listen for storage changes (when contacts are synced on contacts page)
    const handleStorageChange = (e) => {
      if (e.key === 'icloud_contacts') {
        fetchRecentContacts();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Upcoming Flights state
  const [upcomingFlights, setUpcomingFlights] = useState([]);
  const [flightsLoading, setFlightsLoading] = useState(true);

  // Fetch upcoming flights from calendar API (same as Travel page) with mock data fallback
  useEffect(() => {
    const fetchUpcomingFlights = async () => {
      try {
        // First try to fetch from Outlook Calendar API (same as Travel page)
        const startDate = new Date().toISOString().split('T')[0];
        const endDate = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        
        const response = await fetch(`${API_URL}/api/outlook-calendar/desktop-events?start_date=${startDate}&end_date=${endDate}`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success' && Array.isArray(data.events)) {
            // Filter for travel-related events (same logic as Travel page)
            const travelEvents = data.events.filter(event => {
              const subject = (event.subject || '').toLowerCase();
              return subject.includes('flight') || 
                     subject.includes('travel') || 
                     subject.includes('trip') ||
                     subject.includes('vacation') ||
                     subject.includes('airport') ||
                     subject.includes('airline') ||
                     subject.includes('aa ') ||
                     subject.includes('delta') ||
                     subject.includes('united') ||
                     subject.includes('southwest') ||
                     subject.includes('dfw') ||
                     subject.includes('sna') ||
                     subject.includes('lax') ||
                     subject.includes('jfk') ||
                     subject.includes('ord');
            });
            
            if (travelEvents.length > 0) {
              // Convert calendar events to flight cards
              const flights = travelEvents.map(event => {
                const subject = event.subject || '';
                const eventDate = event.start?.dateTime ? new Date(event.start.dateTime) : new Date(event.date);
                
                // Parse flight details from subject
                const isReturn = subject.toLowerCase().includes('return');
                const airlineMatch = subject.match(/\b(AA|DL|UA|WN|American|Delta|United|Southwest)\b/i);
                const airline = airlineMatch ? airlineMatch[0].toUpperCase() : 'AA';
                
                // Parse airports from subject
                const routeMatch = subject.match(/([A-Z]{3})\s*(?:-->|->|to|—)\s*([A-Z]{3})/i);
                const departure = routeMatch ? routeMatch[1].toUpperCase() : 'DFW';
                const arrival = routeMatch ? routeMatch[2].toUpperCase() : 'TBD';
                
                return {
                  id: event.id || `flight-${Date.now()}-${Math.random()}`,
                  destination: arrival,
                  airline: airline === 'AA' ? 'American Airlines' : 
                           airline === 'DL' ? 'Delta Airlines' :
                           airline === 'UA' ? 'United Airlines' :
                           airline === 'WN' ? 'Southwest Airlines' : airline,
                  start_date: eventDate.toISOString().split('T')[0],
                  departure: departure,
                  arrival: arrival,
                  time: eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                  subject: subject,
                  status: eventDate > new Date() ? 'upcoming' : 'completed'
                };
              });
              
              // Filter out deleted flights, sort by date, and take next 3
              const deletedFlights = JSON.parse(localStorage.getItem('deleted_flights') || '[]');
              const upcoming = flights
                .filter(f => !deletedFlights.includes(f.id) && new Date(f.start_date) >= new Date())
                .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                .slice(0, 3);
              
              if (upcoming.length > 0) {
                setUpcomingFlights(upcoming);
                setFlightsLoading(false);
                return;
              }
            }
          }
        }
        
        // Fallback to mock data if no calendar flights found
        const { travelMockData } = await import('../data/mockData');
        const trips = travelMockData || [];
        
        const today = new Date();
        const upcoming = trips
          .filter(t => {
            const startDate = new Date(t.start_date);
            return startDate >= today && (t.status === 'upcoming' || t.status === 'planned');
          })
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
          .slice(0, 3);
        
        setUpcomingFlights(upcoming);
      } catch (error) {
        console.error('Error fetching flights:', error);
        // Use mock data on error
        try {
          const { travelMockData } = await import('../data/mockData');
          const trips = travelMockData || [];
          const today = new Date();
          const upcoming = trips
            .filter(t => new Date(t.start_date) >= today)
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
            .slice(0, 3);
          setUpcomingFlights(upcoming);
        } catch (e) {
          console.error('Error loading mock data:', e);
        }
      } finally {
        setFlightsLoading(false);
      }
    };
    fetchUpcomingFlights();
    
    // Listen for storage changes (when flights are deleted on Travel page)
    const handleStorageChange = (e) => {
      if (e.key === 'deleted_flights') {
        fetchUpcomingFlights();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Format date for display
  const formatFlightDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Upcoming Meetings state
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(true);
  const [meetingsStatusFilter, setMeetingsStatusFilter] = useState('all');
  const [dashboardMeetingStatuses, setDashboardMeetingStatuses] = useState({});

  // Fetch upcoming meetings from Excel data (primary) or meetings API (fallback)
  useEffect(() => {
    const fetchUpcomingMeetings = async () => {
      try {
        // First try Excel meetings (primary source) - use XMLHttpRequest to avoid fetch issues
        const excelData = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', `${API_URL}/api/excel/get-desktop-data`, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch (e) {
                reject(new Error('Invalid response'));
              }
            } else {
              reject(new Error(`HTTP ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send();
        });
        
        if (excelData.status === 'success' && excelData.meetings && excelData.meetings.length > 0) {
          // Process Excel meetings - only show upcoming meetings (future dates only)
          const parseExcelDate = (dateStr) => {
            if (!dateStr || dateStr === 'TBD' || dateStr === '-' || dateStr === '') return null;
            // Handle "2025-11-06 00:00:00" format
            const cleanDate = String(dateStr).split(' ')[0];
            const parsed = new Date(cleanDate + 'T00:00:00');
            return isNaN(parsed.getTime()) ? null : parsed;
          };
          
          const parseExcelTime = (timeVal) => {
            if (!timeVal || timeVal === 'TBD' || timeVal === '-') return '09:00';
            // If it's already a formatted time string (HH:MM or H:MM format), return as-is
            const timeStr = String(timeVal);
            if (/^\d{1,2}:\d{2}/.test(timeStr)) {
              // Already formatted, pad hours if needed
              const [h, m] = timeStr.split(':');
              return `${h.padStart(2, '0')}:${m.substring(0, 2)}`;
            }
            // If it's a decimal (Excel time format 0-1), convert to HH:MM
            if (typeof timeVal === 'number' || !isNaN(parseFloat(timeVal))) {
              const decimal = parseFloat(timeVal);
              // Only convert if it looks like an Excel time fraction (0 to 1)
              if (decimal >= 0 && decimal < 1) {
                const hours = Math.floor(decimal * 24);
                const minutes = Math.round((decimal * 24 - hours) * 60);
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
              }
            }
            return timeVal;
          };
          
          // Get today's date at midnight for comparison
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayTime = today.getTime();
          
          console.log('📅 Today (for comparison):', today.toISOString());
          
          const upcoming = excelData.meetings
            .map(m => {
              const parsedDate = parseExcelDate(m.date);
              const parsedTime = parseExcelTime(m.time);
              const datetime = parsedDate ? new Date(`${parsedDate.toISOString().split('T')[0]}T${parsedTime}:00`) : null;
              
              return {
                ...m,
                id: m.id || m.excel_meeting_id,
                title: m.title || `Meeting with ${m.participants?.[0]?.name || 'Contact'}`,
                datetime: datetime,
                time: parsedTime, // Store formatted time (HH:MM) instead of raw Excel decimal
                category: m.meeting_type || m.category || 'Client',
                type: m.meeting_type || 'Client',
                participants: m.participants || [],
                organization: m.organization || m.company || '',
                location: m.location || '',
                hasValidDate: !!parsedDate,
                dateMs: parsedDate ? parsedDate.getTime() : 0
              };
            })
            // Filter: must have valid date AND date must be today or in the future
            .filter(m => {
              if (!m.hasValidDate) return false;
              // Compare milliseconds to avoid timezone issues
              return m.dateMs >= todayTime;
            })
            // Sort by date (soonest first)
            .sort((a, b) => a.dateMs - b.dateMs)
            // Limit to 5 meetings for dashboard display
            .slice(0, 5);
          
          console.log(`📅 Found ${upcoming.length} upcoming meetings after filter`);
          setUpcomingMeetings(upcoming);
          setMeetingsLoading(false);
          return;
        }
        
        // Fallback to regular meetings API
        const meetingsData = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', `${API_URL}/api/meetings/`, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch (e) {
                resolve([]);
              }
            } else {
              resolve([]);
            }
          };
          xhr.onerror = () => resolve([]);
          xhr.send();
        });
        
        if (Array.isArray(meetingsData) && meetingsData.length > 0) {
          const today = new Date();
          const upcoming = meetingsData
            .filter(m => new Date(m.datetime) >= today)
            .sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
            .slice(0, 10);
          setUpcomingMeetings(upcoming);
          console.log(`📅 Loaded ${upcoming.length} meetings from API`);
        }
      } catch (error) {
        console.error('Error fetching meetings:', error);
      } finally {
        setMeetingsLoading(false);
      }
    };
    fetchUpcomingMeetings();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchUpcomingMeetings, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Load meeting statuses from backend for dashboard
  useEffect(() => {
    const loadMeetingStatuses = async () => {
      try {
        const response = await fetch(`${API_URL}/api/excel/get-meeting-statuses`);
        if (response.ok) {
          const data = await response.json();
          if (data.statuses) {
            setDashboardMeetingStatuses(data.statuses);
          }
        }
      } catch (error) {
        console.log('Could not load meeting statuses');
      }
    };
    loadMeetingStatuses();
  }, []);

  // Get meeting status for dashboard (checks manual status first, then calculates)
  const getDashboardMeetingStatus = (meeting) => {
    // Check for manually set status first
    if (dashboardMeetingStatuses[meeting.id]) {
      return dashboardMeetingStatuses[meeting.id];
    }
    
    // Calculate status based on date
    const dateStr = meeting.date || '';
    if (!dateStr || dateStr === 'TBD' || dateStr === '-') {
      return 'upcoming';
    }
    
    const cleanDate = String(dateStr).split(' ')[0];
    const meetingDate = new Date(cleanDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (isNaN(meetingDate.getTime())) {
      return 'upcoming';
    }
    
    const diffDays = Math.floor((meetingDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'done';
    } else if (diffDays === 0) {
      return 'in_progress';
    }
    return 'upcoming';
  };

  // Listen for Spotify auth success message from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'SPOTIFY_AUTH_SUCCESS') {
        const token = localStorage.getItem('spotify_access_token');
        if (token) {
          setSpotifyToken(token);
          setSpotifyConnected(true);
          setSpotifyLoading(false);
          fetchCurrentTrack(token);
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Spotify Functions
  const connectToSpotify = async () => {
    setSpotifyLoading(true);
    try {
      console.log('🎵 Connecting to Spotify...');
      
      // Use XMLHttpRequest as fallback to avoid fetch issues
      const authUrl = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${API_URL}/api/spotify/auth/login`, true);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.timeout = 10000;
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data.auth_url);
            } catch (e) {
              reject(new Error('Invalid response from server'));
            }
          } else {
            reject(new Error(`Server error: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error - please check your connection'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));
        xhr.send();
      });
      
      if (!authUrl) {
        throw new Error('No auth URL received from server');
      }
      
      console.log('🎵 Got auth URL, opening popup...');
      
      // Open Spotify auth in a popup
      const width = 450;
      const height = 730;
      const left = (window.screen.width / 2) - (width / 2);
      const top = (window.screen.height / 2) - (height / 2);
      const popup = window.open(
        authUrl,
        'Spotify Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      if (!popup) {
        alert('Popup was blocked. Please allow popups for this site to connect to Spotify.');
        setSpotifyLoading(false);
        return;
      }
      
      // Listen for the callback (backup check when popup closes)
      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            setSpotifyLoading(false);
            // Check if token was saved
            const token = localStorage.getItem('spotify_access_token');
            if (token) {
              setSpotifyToken(token);
              setSpotifyConnected(true);
              fetchCurrentTrack(token);
              console.log('🎵 Spotify connected successfully!');
            }
          }
        } catch (e) {
          // Cross-origin error expected
        }
      }, 500);
      
    } catch (error) {
      console.error('🎵 Spotify connection error:', error);
      alert(`Failed to connect to Spotify: ${error.message}`);
      setSpotifyLoading(false);
    }
  };

  const fetchCurrentTrack = async (token) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/spotify/playback/state?access_token=${encodeURIComponent(token)}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.item) {
          const track = data.item;
          const artistIds = track.artists?.map(a => a.id) || [];
          const trackId = track.id;
          
          // Check if track actually changed - only clear cached data if different track
          const prevTrackId = currentTrackIdRef.current;
          if (prevTrackId !== null && prevTrackId !== trackId) {
            // Track changed - clear the cached artist/track info so it gets refetched
            setSpotifyArtistInfo(null);
            setSpotifyTrackDetails(null);
          }
          currentTrackIdRef.current = trackId;
          
          setCurrentTrack({
            id: track.id,
            name: track.name,
            artist: track.artists?.map(a => a.name).join(', ') || 'Unknown Artist',
            artistIds: artistIds,
            album: track.album?.name || 'Unknown Album',
            albumId: track.album?.id,
            album_art: track.album?.images?.[0]?.url || null,
            album_images: track.album?.images || [],
            duration: track.duration_ms,
            progress: data.progress_ms || 0,
            uri: track.uri,
            popularity: track.popularity,
            explicit: track.explicit,
            release_date: track.album?.release_date
          });
          setIsPlaying(data.is_playing || false);
          if (!isSeeking) {
            setSpotifyProgress(data.progress_ms || 0);
          }
          setSpotifyShuffle(data.shuffle_state || false);
          setSpotifyRepeat(data.repeat_state || 'off');
          if (data.device) {
            setActiveDevice(data.device);
            setSpotifyVolume(data.device.volume_percent || 50);
          }
        } else {
          setCurrentTrack(null);
          setIsPlaying(false);
        }
      }
    } catch (error) {
      console.error('Error fetching current track:', error);
    }
  };

  const fetchArtistInfo = async (artistId) => {
    if (!spotifyToken || !artistId) return;
    // Prevent duplicate fetches
    if (fetchingArtistIdRef.current === artistId) return;
    fetchingArtistIdRef.current = artistId;
    
    setSpotifyLoadingDetails(true);
    try {
      const response = await fetch(`${API_URL}/api/spotify/artist/${artistId}?access_token=${encodeURIComponent(spotifyToken)}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Artist info fetched:', data);
        setSpotifyArtistInfo(data);
      } else {
        console.error('Artist fetch failed:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error fetching artist info:', error);
    } finally {
      setSpotifyLoadingDetails(false);
      fetchingArtistIdRef.current = null;
    }
  };

  const fetchTrackDetails = async (trackId) => {
    if (!spotifyToken || !trackId) return;
    // Prevent duplicate fetches
    if (fetchingTrackIdRef.current === trackId) return;
    fetchingTrackIdRef.current = trackId;
    
    setSpotifyLoadingDetails(true);
    try {
      const response = await fetch(`${API_URL}/api/spotify/track/${trackId}?access_token=${encodeURIComponent(spotifyToken)}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Track details fetched:', data);
        setSpotifyTrackDetails(data);
      } else {
        console.error('Track details fetch failed:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error fetching track details:', error);
    } finally {
      setSpotifyLoadingDetails(false);
      fetchingTrackIdRef.current = null;
    }
  };

  const fetchSpotifyDevices = async (token) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/spotify/devices?access_token=${encodeURIComponent(token)}`);
      if (response.ok) {
        const data = await response.json();
        setSpotifyDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  const spotifyPlayPause = async () => {
    if (!spotifyToken) return;
    try {
      const endpoint = isPlaying ? 'pause' : 'play';
      const response = await fetch(`${API_URL}/api/spotify/playback/${endpoint}?access_token=${encodeURIComponent(spotifyToken)}`, {
        method: 'POST'
      });
      if (response.ok) {
        setIsPlaying(!isPlaying);
        setTimeout(() => fetchCurrentTrack(spotifyToken), 300);
      } else {
        console.error('Spotify playback error:', await response.text());
      }
    } catch (error) {
      console.error('Spotify playback error:', error);
    }
  };

  const spotifyNext = async () => {
    if (!spotifyToken) return;
    try {
      const response = await fetch(`${API_URL}/api/spotify/playback/next?access_token=${encodeURIComponent(spotifyToken)}`, {
        method: 'POST'
      });
      if (response.ok) {
        setTimeout(() => fetchCurrentTrack(spotifyToken), 500);
      }
    } catch (error) {
      console.error('Spotify next error:', error);
    }
  };

  const spotifyPrev = async () => {
    if (!spotifyToken) return;
    try {
      const response = await fetch(`${API_URL}/api/spotify/playback/previous?access_token=${encodeURIComponent(spotifyToken)}`, {
        method: 'POST'
      });
      if (response.ok) {
        setTimeout(() => fetchCurrentTrack(spotifyToken), 500);
      }
    } catch (error) {
      console.error('Spotify previous error:', error);
    }
  };

  const spotifySetVolume = async (vol) => {
    if (!spotifyToken) return;
    setSpotifyVolume(vol);
    try {
      await fetch(`${API_URL}/api/spotify/playback/volume?access_token=${encodeURIComponent(spotifyToken)}&volume=${vol}`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Spotify volume error:', error);
    }
  };

  const spotifyToggleShuffle = async () => {
    if (!spotifyToken) return;
    const newState = !spotifyShuffle;
    setSpotifyShuffle(newState);
    try {
      await fetch(`${API_URL}/api/spotify/playback/shuffle?access_token=${encodeURIComponent(spotifyToken)}&state=${newState}`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Spotify shuffle error:', error);
    }
  };

  const spotifyToggleRepeat = async () => {
    if (!spotifyToken) return;
    const states = ['off', 'context', 'track'];
    const currentIdx = states.indexOf(spotifyRepeat);
    const newState = states[(currentIdx + 1) % states.length];
    setSpotifyRepeat(newState);
    try {
      await fetch(`${API_URL}/api/spotify/playback/repeat?access_token=${encodeURIComponent(spotifyToken)}&state=${newState}`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Spotify repeat error:', error);
    }
  };

  const spotifySeek = async (positionMs) => {
    if (!spotifyToken) return;
    setIsSeeking(true);
    setSpotifyProgress(positionMs);
    try {
      await fetch(`${API_URL}/api/spotify/playback/seek?access_token=${encodeURIComponent(spotifyToken)}&position_ms=${positionMs}`, {
        method: 'POST'
      });
      setTimeout(() => {
        setIsSeeking(false);
        fetchCurrentTrack(spotifyToken);
      }, 500);
    } catch (error) {
      console.error('Spotify seek error:', error);
      setIsSeeking(false);
    }
  };

  const spotifyTransferPlayback = async (deviceId) => {
    if (!spotifyToken) return;
    try {
      await fetch(`${API_URL}/api/spotify/playback/transfer?access_token=${encodeURIComponent(spotifyToken)}&device_id=${deviceId}&play=true`, {
        method: 'POST'
      });
      setShowDevicesDropdown(false);
      setTimeout(() => {
        fetchCurrentTrack(spotifyToken);
        fetchSpotifyDevices(spotifyToken);
      }, 500);
    } catch (error) {
      console.error('Spotify transfer error:', error);
    }
  };

  const disconnectSpotify = () => {
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    setSpotifyToken(null);
    setSpotifyConnected(false);
    setCurrentTrack(null);
    setIsPlaying(false);
    setSpotifyDevices([]);
    setActiveDevice(null);
  };

  // Poll for current track when connected - faster polling (3 seconds) for better real-time sync
  useEffect(() => {
    if (spotifyConnected && spotifyToken) {
      fetchCurrentTrack(spotifyToken);
      fetchSpotifyDevices(spotifyToken);
      const interval = setInterval(() => fetchCurrentTrack(spotifyToken), 3000);
      return () => clearInterval(interval);
    }
  }, [spotifyConnected, spotifyToken]);

  // Progress bar animation when playing
  useEffect(() => {
    if (isPlaying && currentTrack && !isSeeking) {
      const progressInterval = setInterval(() => {
        setSpotifyProgress(prev => {
          if (prev >= (currentTrack.duration || 0)) return prev;
          return prev + 1000;
        });
      }, 1000);
      return () => clearInterval(progressInterval);
    }
  }, [isPlaying, currentTrack, isSeeking]);

  // Weather fetching with geolocation
  useEffect(() => {
    const fetchWeather = async (lat, lon) => {
      try {
        // Fetch weather from Open-Meteo API (free, no API key needed)
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m,uv_index&daily=weather_code,temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto&forecast_days=5`
        );
        
        if (response.ok) {
          const data = await response.json();
          setWeatherData(data);
          setWeatherError(null);
        } else {
          throw new Error('Weather fetch failed');
        }
      } catch (error) {
        console.error('Weather fetch error:', error);
        setWeatherError('Unable to load weather');
      } finally {
        setWeatherLoading(false);
      }
    };

    const getLocationName = async (lat, lon) => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
        );
        if (response.ok) {
          const data = await response.json();
          const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || 'Unknown';
          const state = data.address?.state || '';
          setWeatherLocation(`${city}${state ? ', ' + state : ''}`);
        }
      } catch (error) {
        console.error('Location name fetch error:', error);
      }
    };

    // Get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetchWeather(latitude, longitude);
          getLocationName(latitude, longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Fallback to Dallas, TX
          fetchWeather(32.7767, -96.7970);
          setWeatherLocation('Dallas, TX');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      // Fallback if geolocation not supported - use Dallas, TX
      fetchWeather(32.7767, -96.7970);
      setWeatherLocation('Dallas, TX');
    }
  }, []);

  // Helper function to get weather description from WMO code
  const getWeatherDescription = (code) => {
    const weatherCodes = {
      0: 'Clear Sky',
      1: 'Mainly Clear',
      2: 'Partly Cloudy',
      3: 'Overcast',
      45: 'Foggy',
      48: 'Icy Fog',
      51: 'Light Drizzle',
      53: 'Drizzle',
      55: 'Heavy Drizzle',
      56: 'Freezing Drizzle',
      57: 'Heavy Freezing Drizzle',
      61: 'Light Rain',
      63: 'Rain',
      65: 'Heavy Rain',
      66: 'Freezing Rain',
      67: 'Heavy Freezing Rain',
      71: 'Light Snow',
      73: 'Snow',
      75: 'Heavy Snow',
      77: 'Snow Grains',
      80: 'Light Showers',
      81: 'Showers',
      82: 'Heavy Showers',
      85: 'Light Snow Showers',
      86: 'Heavy Snow Showers',
      95: 'Thunderstorm',
      96: 'Thunderstorm with Hail',
      99: 'Heavy Thunderstorm'
    };
    return weatherCodes[code] || 'Unknown';
  };

  // Helper function to get weather icon from WMO code
  const getWeatherIcon = (code, size = 'w-6 h-6') => {
    // Clear/Sunny
    if (code === 0 || code === 1) {
      return (
        <svg className={`${size} text-yellow-400`} fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="5"/>
          <path d="M12 2v2m0 16v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M2 12h2m16 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
        </svg>
      );
    }
    // Partly Cloudy
    if (code === 2) {
      return (
        <svg className={`${size} text-white`} fill="currentColor" viewBox="0 0 24 24">
          <circle cx="8" cy="8" r="3" className="text-yellow-400"/>
          <path d="M6.5 20C4.567 20 3 18.433 3 16.5c0-1.857 1.446-3.375 3.285-3.485C6.106 11.827 7.33 11 8.764 11c.827 0 1.577.327 2.144.855C11.377 10.227 12.586 9 14.5 9c2.481 0 4.5 2.019 4.5 4.5 0 .17-.01.338-.03.503C20.177 14.353 21 15.589 21 17c0 1.657-1.343 3-3 3H6.5z"/>
        </svg>
      );
    }
    // Cloudy/Overcast
    if (code === 3 || code === 45 || code === 48) {
      return (
        <svg className={`${size} text-gray-300`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M6.5 20C4.567 20 3 18.433 3 16.5c0-1.857 1.446-3.375 3.285-3.485C6.106 11.827 7.33 11 8.764 11c.827 0 1.577.327 2.144.855C11.377 10.227 12.586 9 14.5 9c2.481 0 4.5 2.019 4.5 4.5 0 .17-.01.338-.03.503C20.177 14.353 21 15.589 21 17c0 1.657-1.343 3-3 3H6.5z"/>
        </svg>
      );
    }
    // Rain
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) {
      return (
        <svg className={`${size} text-white`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M6.5 16C4.567 16 3 14.433 3 12.5c0-1.857 1.446-3.375 3.285-3.485C6.106 7.827 7.33 7 8.764 7c.827 0 1.577.327 2.144.855C11.377 6.227 12.586 5 14.5 5c2.481 0 4.5 2.019 4.5 4.5 0 .17-.01.338-.03.503C20.177 10.353 21 11.589 21 13c0 1.657-1.343 3-3 3H6.5z"/>
          <path d="M8 18v2m4-4v4m4-2v2" strokeWidth="2" stroke="currentColor" strokeLinecap="round"/>
        </svg>
      );
    }
    // Snow
    if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) {
      return (
        <svg className={`${size} text-white`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M6.5 16C4.567 16 3 14.433 3 12.5c0-1.857 1.446-3.375 3.285-3.485C6.106 7.827 7.33 7 8.764 7c.827 0 1.577.327 2.144.855C11.377 6.227 12.586 5 14.5 5c2.481 0 4.5 2.019 4.5 4.5 0 .17-.01.338-.03.503C20.177 10.353 21 11.589 21 13c0 1.657-1.343 3-3 3H6.5z"/>
          <circle cx="8" cy="19" r="1"/>
          <circle cx="12" cy="20" r="1"/>
          <circle cx="16" cy="18" r="1"/>
        </svg>
      );
    }
    // Thunderstorm
    if (code >= 95) {
      return (
        <svg className={`${size} text-yellow-500`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M6.5 14C4.567 14 3 12.433 3 10.5c0-1.857 1.446-3.375 3.285-3.485C6.106 5.827 7.33 5 8.764 5c.827 0 1.577.327 2.144.855C11.377 4.227 12.586 3 14.5 3c2.481 0 4.5 2.019 4.5 4.5 0 .17-.01.338-.03.503C20.177 8.353 21 9.589 21 11c0 1.657-1.343 3-3 3H6.5z" className="text-gray-400"/>
          <path d="M13 12l-2 5h3l-2 5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    // Default cloud
    return (
      <svg className={`${size} text-white`} fill="currentColor" viewBox="0 0 24 24">
        <path d="M6.5 20C4.567 20 3 18.433 3 16.5c0-1.857 1.446-3.375 3.285-3.485C6.106 11.827 7.33 11 8.764 11c.827 0 1.577.327 2.144.855C11.377 10.227 12.586 9 14.5 9c2.481 0 4.5 2.019 4.5 4.5 0 .17-.01.338-.03.503C20.177 14.353 21 15.589 21 17c0 1.657-1.343 3-3 3H6.5z"/>
      </svg>
    );
  };

  // Get weather background gradient based on weather code, temperature, and time of day
  const getWeatherBackground = (code, temp = null) => {
    const currentHour = new Date().getHours();
    const isNight = currentHour < 6 || currentHour >= 19;
    const isDusk = currentHour >= 17 && currentHour < 19;
    const isDawn = currentHour >= 6 && currentHour < 8;
    const isCold = temp !== null && temp < 40; // Below 40°F is cold
    const isVeryCold = temp !== null && temp < 20; // Below 20°F is very cold
    
    // Night time - dark blue
    if (isNight) {
      if (code === 0 || code === 1) {
        // Clear night - dark navy blue
        return 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)';
      }
      // Cloudy/rainy night - darker blue
      return 'linear-gradient(135deg, #111827 0%, #0F172A 100%)';
    }
    
    // Dawn - soft blue (no pink/purple)
    if (isDawn && (code === 0 || code === 1 || code === 2)) {
      return 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)';
    }
    
    // Dusk/Sunset - soft blue (no orange/red)
    if (isDusk && (code === 0 || code === 1 || code === 2)) {
      return 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)';
    }
    
    // Clear/Sunny daytime - blue
    if (code === 0 || code === 1) {
      if (isVeryCold) {
        // Very cold clear day - deep blue
        return 'linear-gradient(135deg, #0284C7 0%, #0369A1 100%)';
      }
      if (isCold) {
        // Cold clear day - medium blue
        return 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)';
      }
      // Warm clear day - bright blue
      return 'linear-gradient(135deg, #38BDF8 0%, #0EA5E9 100%)';
    }
    
    // Partly Cloudy - blue
    if (code === 2) {
      if (isCold) {
        // Cold partly cloudy - blue-gray
        return 'linear-gradient(135deg, #64748B 0%, #475569 100%)';
      }
      return 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)';
    }
    
    // Cloudy/Overcast - gray
    if (code === 3 || code === 45 || code === 48) {
      return 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)';
    }
    
    // Rain/Drizzle - dark gray
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) {
      return 'linear-gradient(135deg, #475569 0%, #334155 100%)';
    }
    
    // Snow - light gray
    if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) {
      return 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)';
    }
    
    // Thunderstorm - dark purple-blue
    if (code >= 95) {
      return 'linear-gradient(135deg, #3730A3 0%, #312E81 100%)';
    }
    
    // Default - bright blue
    return 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)';
  };

  // Format meeting datetime
  const formatMeetingDateTime = (dateInput) => {
    // Handle null/undefined
    if (!dateInput) {
      return { date: 'TBD', time: 'TBD' };
    }
    
    let date;
    
    // Handle different date formats
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
      date = dateInput;
    } else if (typeof dateInput === 'string') {
      // Clean the date string first
      const cleanStr = String(dateInput).split(' ')[0]; // Handle "2026-01-29 00:00:00" format
      // Try parsing with time to ensure proper timezone
      date = new Date(cleanStr + 'T12:00:00');
      // If still invalid, try basic parsing
      if (isNaN(date.getTime())) {
        date = new Date(cleanStr);
      }
    } else if (typeof dateInput === 'number') {
      // Handle timestamps
      date = new Date(dateInput);
    } else {
      return { date: 'TBD', time: 'TBD' };
    }
    
    // Check if date is valid
    if (!date || isNaN(date.getTime())) {
      return { date: 'TBD', time: 'TBD' };
    }
    
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  // Get meeting type color
  const getMeetingTypeColor = (type) => {
    const colors = {
      'Client': 'bg-blue-500',
      'Team': 'bg-purple-500',
      'Investor': 'bg-green-500',
      'Interview': 'bg-orange-500',
      'Property': 'bg-cyan-500',
      'Conference': 'bg-pink-500',
      'Personal': 'bg-gray-500',
    };
    return colors[type] || 'bg-gray-500';
  };

  // Filter meetings by status
  const filteredMeetings = meetingsStatusFilter === 'all' 
    ? upcomingMeetings 
    : upcomingMeetings.filter(m => getDashboardMeetingStatus(m) === meetingsStatusFilter);

  // Calendar helpers (uses existing calendarDate state)
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const prevMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           calendarDate.getMonth() === today.getMonth() && 
           calendarDate.getFullYear() === today.getFullYear();
  };

  // Get category color for contact
  const getCategoryColor = (category) => {
    const colors = {
      // Original categories
      'Investor': 'bg-purple-500',
      'Client': 'bg-blue-500',
      'Tenant': 'bg-green-500',
      'Vendor': 'bg-orange-500',
      'Partner': 'bg-pink-500',
      'Lead': 'bg-yellow-500',
      'Agent': 'bg-cyan-500',
      // iCloud contact categories
      'Passwords': 'bg-red-600',
      'Real Estate': 'bg-emerald-600',
      'Healthcare': 'bg-blue-600',
      'Technology': 'bg-purple-600',
      'Finance': 'bg-green-600',
      'Sports': 'bg-orange-600',
      'Legal': 'bg-yellow-600',
      'Education': 'bg-indigo-600',
      'Services': 'bg-teal-600',
      'Automotive': 'bg-red-700',
      'Family': 'bg-pink-600',
      'Friends': 'bg-cyan-600',
      'Other': 'bg-gray-600',
    };
    return colors[category] || 'bg-gray-500';
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // AI Assistant state
  const [aiMessages, setAiMessages] = useState(() => {
    // Load current conversation from localStorage on init
    const saved = localStorage.getItem('ai_current_conversation');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return []; // Start empty, will be populated by daily brief
      }
    }
    return []; // Start empty, will be populated by daily brief
  });
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSessionId] = useState(() => `dashboard-${Date.now()}`);
  const [aiModel, setAiModel] = useState('gpt'); // 'gpt', 'gemini', 'perplexity', 'claude'
  const aiChatRef = useRef(null);
  const dailyBriefShownRef = useRef(false); // Track if daily brief has been shown this session
  const [aiVaultCount, setAiVaultCount] = useState(() => {
    const saved = localStorage.getItem('ai_vault_conversations');
    if (saved) {
      try {
        return JSON.parse(saved).length;
      } catch (e) {
        return 0;
      }
    }
    return 0;
  });
  const [aiExpanded, setAiExpanded] = useState(false);
  const [savedAiConversations, setSavedAiConversations] = useState(() => {
    // Load saved conversations from localStorage on init
    const saved = localStorage.getItem('ai_vault_conversations');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [showVaultModal, setShowVaultModal] = useState(false);
  
  // AI Assistant toolbar states
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentUploaded, setAttachmentUploaded] = useState(false);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Common emojis for quick selection - organized in rows
  const commonEmojis = [
    ['😊', '😂', '🥰', '😎', '🤔'],
    ['👍', '👋', '🙏', '💪', '🤝'],
    ['🎉', '⭐', '🔥', '💡', '✨'],
    ['💼', '📊', '📈', '💰', '🏠'],
    ['✅', '❌', '📞', '📧', '⏰']
  ];

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    if (aiChatRef.current) {
      aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight;
    }
  }, [aiMessages]);

  // Save current conversation to localStorage whenever messages change
  useEffect(() => {
    if (aiMessages.length > 0) {
      localStorage.setItem('ai_current_conversation', JSON.stringify(aiMessages));
    }
  }, [aiMessages]);

  // Save vault conversations to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('ai_vault_conversations', JSON.stringify(savedAiConversations));
    setAiVaultCount(savedAiConversations.length);
  }, [savedAiConversations]);

  // Send message to AI
  const sendAiMessage = async () => {
    if ((!aiInput.trim() && !attachedFile) || aiLoading) return;
    
    const userMessage = aiInput.trim();
    const hasAttachment = attachedFile && attachmentUploaded;
    
    // Build message with attachment info if present
    let fullMessage = userMessage;
    if (hasAttachment) {
      fullMessage = userMessage || `Please summarize this file: ${attachedFile.name}`;
    }
    
    setAiInput('');
    setAiMessages(prev => [...prev, { 
      role: 'user', 
      content: fullMessage,
      attachment: hasAttachment ? { name: attachedFile.name, uploaded: true } : null
    }]);
    setAiLoading(true);
    
    // Check if user wants to send file to documents - expanded keywords
    const messageLower = userMessage.toLowerCase();
    const wantsToSendToDocs = hasAttachment && (
      messageLower.includes('document') ||
      messageLower.includes('save') ||
      messageLower.includes('add') ||
      messageLower.includes('store') ||
      messageLower.includes('upload') ||
      messageLower.includes('move') ||
      messageLower.includes('put') ||
      messageLower.includes('send')
    );
    
    try {
      if (hasAttachment && wantsToSendToDocs) {
        // File is already uploaded, just confirm
        setAiMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `✅ Done! I've saved "${attachedFile.name}" to your Documents page. You can find it in the root folder at /documents-re.\n\n📁 [View Documents](/documents-re)`
        }]);
        removeAttachment();
      } else if (hasAttachment) {
        // Show processing message immediately for file analysis
        const processingMsgIndex = aiMessages.length + 1;
        setAiMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `📄 Analyzing "${attachedFile.name}"... Please wait.`,
          isProcessing: true
        }]);
        
        // Use Gemini for file analysis
        const formData = new FormData();
        formData.append('file', attachedFile);
        formData.append('message', userMessage || `Please analyze and summarize this file: ${attachedFile.name}`);
        formData.append('session_id', aiSessionId);
        
        const response = await fetch(`${API_URL}/api/ai-assistant/chat-with-file`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Failed to analyze file');
        }
        
        const data = await response.json();
        // Replace processing message with actual response
        setAiMessages(prev => {
          const updated = [...prev];
          // Find and replace the processing message
          const processingIdx = updated.findIndex(m => m.isProcessing);
          if (processingIdx !== -1) {
            updated[processingIdx] = { role: 'assistant', content: data.response };
          } else {
            updated.push({ role: 'assistant', content: data.response });
          }
          return updated;
        });
        removeAttachment();
      } else {
        // Regular AI chat (no file)
        const response = await apiCall('/ai-assistant/chat', {
          method: 'POST',
          body: JSON.stringify({
            session_id: aiSessionId,
            message: fullMessage,
            model: aiModel  // Send selected model (gpt, gemini, perplexity)
          })
        });
        
        setAiMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Generate daily brief based on tasks, events, meetings, etc.
  const generateDailyBrief = () => {
    const today = new Date();
    const hour = today.getHours();
    const currentMinutes = today.getMinutes();
    
    // Helper to check if a meeting time has passed
    const isMeetingPast = (meetingTime) => {
      if (!meetingTime || meetingTime === 'TBD' || meetingTime === '-') return false;
      // Handle HH:MM format
      const timeParts = String(meetingTime).split(':');
      if (timeParts.length >= 2) {
        const meetingHour = parseInt(timeParts[0]);
        const meetingMinute = parseInt(timeParts[1]);
        if (!isNaN(meetingHour) && !isNaN(meetingMinute)) {
          return (hour > meetingHour) || (hour === meetingHour && currentMinutes > meetingMinute);
        }
      }
      return false;
    };
    
    // Get the user's first name from profile
    const firstName = profileName ? profileName.split(' ')[0] : 'Darren';
    
    // Get today's events (already deduplicated from the fetch) - only upcoming ones
    const events = todayOutlookEvents || [];
    const upcomingEvents = events.filter(e => !isEventPast(e.time) && !completedEventIds.includes(e.id));
    const completedEvents = events.filter(e => isEventPast(e.time) || completedEventIds.includes(e.id));
    
    // Get meetings for TODAY only (local date)
    const meetings = upcomingMeetings || [];
    
    const todayMeetings = meetings.filter(m => {
      // First try datetime, then fall back to date string
      let meetingDate;
      if (m.datetime && m.datetime instanceof Date && !isNaN(m.datetime.getTime())) {
        meetingDate = m.datetime;
      } else if (m.date) {
        // Parse date string (YYYY-MM-DD format) using local timezone
        const dateParts = String(m.date).split('-');
        if (dateParts.length === 3) {
          meetingDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        } else {
          meetingDate = new Date(m.date);
        }
      } else {
        return false;
      }
      
      if (isNaN(meetingDate.getTime())) {
        return false;
      }
      
      // Compare using local date components
      const isToday = meetingDate.getFullYear() === today.getFullYear() &&
             meetingDate.getMonth() === today.getMonth() &&
             meetingDate.getDate() === today.getDate();
      
      // Also check if meeting time has passed - only show upcoming meetings
      if (isToday && isMeetingPast(m.time)) {
        return false;
      }
      
      return isToday;
    });
    
    // Combine events and meetings, deduplicate by title+time, sort by time, take top 5
    const allItemsMap = new Map();
    
    // Add events
    upcomingEvents.forEach(e => {
      const key = `${(e.subject || '').toLowerCase()}-${e.time || '00:00'}`;
      if (!allItemsMap.has(key)) {
        allItemsMap.set(key, {
          time: e.time || '00:00',
          title: e.subject,
          type: 'event'
        });
      }
    });
    
    // Add meetings (won't add if same title+time exists)
    todayMeetings.forEach(m => {
      const meetingTitle = m.client || m.title || 'Meeting';
      // Format time: handle Excel decimal format (e.g., 0.5416666666666666 = 13:00)
      let meetingTime = m.time || '00:00';
      const timeStr = String(meetingTime);
      // Check if already formatted (HH:MM or H:MM)
      if (!/^\d{1,2}:\d{2}/.test(timeStr)) {
        // Not formatted, try to parse as Excel decimal
        const decimal = parseFloat(meetingTime);
        if (!isNaN(decimal) && decimal >= 0 && decimal < 1) {
          const hours = Math.floor(decimal * 24);
          const minutes = Math.round((decimal * 24 - hours) * 60);
          meetingTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
      } else {
        // Already formatted, just normalize it
        const [h, mm] = timeStr.split(':');
        meetingTime = `${h.padStart(2, '0')}:${mm.substring(0, 2)}`;
      }
      const key = `${meetingTitle.toLowerCase()}-${meetingTime}`;
      if (!allItemsMap.has(key)) {
        allItemsMap.set(key, {
          time: meetingTime,
          title: meetingTitle,
          type: 'meeting'
        });
      }
    });
    
    const allItems = Array.from(allItemsMap.values())
      .sort((a, b) => {
        // Use parseTimeToMinutes for proper time ordering (handles AM/PM, 24-hour formats)
        const aMinutes = parseTimeToMinutes(a.time);
        const bMinutes = parseTimeToMinutes(b.time);
        return aMinutes - bMinutes;
      })
      .slice(0, 5);
    
    // Build the brief
    let brief = `**Welcome back, ${firstName}!**\n\n`;
    brief += `Here's your personalized daily briefing:\n\n`;
    
    // Show top 5 meetings/events
    if (allItems.length > 0) {
      allItems.forEach(item => {
        const icon = item.type === 'meeting' ? '🤝' : '📅';
        brief += `${icon} **${item.time}** - ${item.title}\n`;
      });
    } else {
      brief += `📅 No meetings or events scheduled for today.\n`;
    }
    
    // Creative progress sentence based on time of day and schedule
    brief += `\n`;
    const totalItems = events.length + todayMeetings.length;
    const completedCount = completedEvents.length;
    
    if (totalItems === 0) {
      // No items - creative messages based on time of day
      if (hour < 12) {
        brief += `Your calendar is wide open this morning — a blank canvas ready for your biggest ideas.`;
      } else if (hour < 17) {
        brief += `No commitments on the books — the afternoon is yours to conquer.`;
      } else {
        brief += `A quiet evening ahead — time to recharge or tackle something you've been putting off.`;
      }
    } else if (completedCount === totalItems) {
      brief += `You've crushed every single item on today's agenda — that's the energy of a true closer! 🔥`;
    } else if (completedCount > totalItems * 0.75) {
      brief += `Almost there! You're in the home stretch with just ${totalItems - completedCount} item${totalItems - completedCount !== 1 ? 's' : ''} left to finish strong.`;
    } else if (completedCount > totalItems * 0.5) {
      brief += `Halfway through and building momentum — keep that winning streak alive!`;
    } else if (completedCount > 0) {
      brief += `You've knocked out ${completedCount} already — ${totalItems - completedCount} more and you'll own this day.`;
    } else {
      // Nothing completed yet
      if (hour < 10) {
        brief += `Fresh start, full slate — ${totalItems} item${totalItems !== 1 ? 's' : ''} ready for you to dominate.`;
      } else if (hour < 14) {
        brief += `${totalItems} item${totalItems !== 1 ? 's' : ''} lined up and waiting — time to make some magic happen.`;
      } else {
        brief += `Still ${totalItems} item${totalItems !== 1 ? 's' : ''} on deck — plenty of time to finish strong before the day wraps.`;
      }
    }
    
    brief += `\n\nHow can I assist you today?`;
    
    return brief;
  };

  // Auto-show daily brief on first load (when no saved conversation or on new session)
  useEffect(() => {
    // Only run once per session when data is ready
    if (dailyBriefShownRef.current) return;
    
    // Wait for essential data to be loaded (not loading state)
    const dataLoaded = !outlookEventsLoading && !meetingsLoading;
    
    if (dataLoaded) {
      // Check if we have a saved conversation
      const savedConvo = localStorage.getItem('ai_current_conversation');
      const hasSavedConvo = savedConvo && JSON.parse(savedConvo).length > 0;
      
      // Check if the last session was today (to avoid showing brief multiple times on same day)
      const lastBriefDate = localStorage.getItem('last_daily_brief_date');
      const todayStr = new Date().toDateString();
      const isNewDay = lastBriefDate !== todayStr;
      
      // Show daily brief if: no saved conversation OR it's a new day
      if (!hasSavedConvo || isNewDay) {
        const dailyBrief = generateDailyBrief();
        const defaultMessage = [{ role: 'assistant', content: dailyBrief }];
        setAiMessages(defaultMessage);
        localStorage.setItem('ai_current_conversation', JSON.stringify(defaultMessage));
        localStorage.setItem('last_daily_brief_date', todayStr);
      } else if (hasSavedConvo) {
        // Load existing conversation
        setAiMessages(JSON.parse(savedConvo));
      }
      
      dailyBriefShownRef.current = true;
    }
  }, [outlookEventsLoading, meetingsLoading, profileName, todayOutlookEvents, upcomingMeetings, upcomingFlights, completedEventIds]);

  // Clear AI chat with personalized daily brief
  const clearAiChat = () => {
    const dailyBrief = generateDailyBrief();
    const defaultMessage = [{ role: 'assistant', content: dailyBrief }];
    setAiMessages(defaultMessage);
    localStorage.setItem('ai_current_conversation', JSON.stringify(defaultMessage));
  };

  // Save AI conversation to vault
  const saveAiConversation = () => {
    if (aiMessages.length > 1) {
      // Generate a title from the first user message
      const firstUserMsg = aiMessages.find(m => m.role === 'user');
      const title = firstUserMsg 
        ? firstUserMsg.content.substring(0, 50) + (firstUserMsg.content.length > 50 ? '...' : '')
        : `Conversation ${savedAiConversations.length + 1}`;
      
      const newConversation = {
        id: Date.now(),
        title: title,
        messages: [...aiMessages],
        savedAt: new Date().toISOString(),
        messageCount: aiMessages.length
      };
      
      const updatedConversations = [...savedAiConversations, newConversation];
      setSavedAiConversations(updatedConversations);
      localStorage.setItem('ai_vault_conversations', JSON.stringify(updatedConversations));
      
      // Show confirmation
      alert(`✅ Conversation saved to vault! (${aiMessages.length} messages)`);
    } else {
      alert('No conversation to save yet. Start chatting first!');
    }
  };

  // Load a conversation from vault
  const loadConversationFromVault = (conversation) => {
    setAiMessages(conversation.messages);
    localStorage.setItem('ai_current_conversation', JSON.stringify(conversation.messages));
    setShowVaultModal(false);
  };

  // Delete a conversation from vault
  const deleteConversationFromVault = (conversationId) => {
    const updatedConversations = savedAiConversations.filter(c => c.id !== conversationId);
    setSavedAiConversations(updatedConversations);
    localStorage.setItem('ai_vault_conversations', JSON.stringify(updatedConversations));
  };

  // Handle file attachment - store file for AI analysis (don't auto-upload to documents)
  const handleFileAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 20MB for Gemini)
      if (file.size > 20 * 1024 * 1024) {
        alert('File too large. Maximum size is 20MB.');
        return;
      }
      
      // Check supported file types
      const supportedTypes = ['pdf', 'txt', 'csv', 'json', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'doc', 'docx', 'xls', 'xlsx'];
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!supportedTypes.includes(ext)) {
        alert(`File type .${ext} is not supported. Supported types: ${supportedTypes.join(', ')}`);
        return;
      }
      
      setAttachedFile(file);
      setAttachmentUploaded(true); // Mark as ready for analysis
      setUploadingAttachment(false);
      console.log('📎 File attached for AI analysis:', file.name, `(${(file.size / 1024).toFixed(1)} KB)`);
    }
  };
  
  // Remove attachment
  const removeAttachment = () => {
    setAttachedFile(null);
    setAttachmentUploaded(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    setAiInput(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Handle voice recording (Speech-to-Text)
  const toggleVoiceRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
    } else {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          stream.getTracks().forEach(track => track.stop());
          
          // Use Web Speech API for transcription
          if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognition = new SpeechRecognition();
            recognition.continuous = false;
            recognition.interimResults = false;
            
            recognition.onresult = (event) => {
              const transcript = event.results[0][0].transcript;
              setAiInput(prev => prev + transcript);
            };
            
            recognition.onerror = (event) => {
              console.error('Speech recognition error:', event.error);
              alert('Voice recognition not available. Please type your message.');
            };
            
            recognition.start();
          } else {
            alert('Voice recognition is not supported in this browser.');
          }
        };

        mediaRecorder.start();
        setIsRecording(true);
        
        // Also use Web Speech API directly for better results
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
          const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
          const recognition = new SpeechRecognition();
          recognition.continuous = true;
          recognition.interimResults = true;
          
          recognition.onresult = (event) => {
            let finalTranscript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
              if (event.results[i].isFinal) {
                finalTranscript += event.results[i][0].transcript;
              }
            }
            if (finalTranscript) {
              setAiInput(prev => prev + finalTranscript);
            }
          };
          
          recognition.start();
          mediaRecorderRef.current.recognition = recognition;
        }
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Unable to access microphone. Please check permissions.');
      }
    }
  };

  // Handle text-to-speech for AI responses
  const speakLastResponse = () => {
    const lastAssistantMessage = [...aiMessages].reverse().find(m => m.role === 'assistant');
    if (lastAssistantMessage && 'speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      } else {
        const utterance = new SpeechSynthesisUtterance(lastAssistantMessage.content);
        utterance.rate = 1;
        utterance.pitch = 1;
        utterance.volume = volume / 100;
        
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        
        window.speechSynthesis.speak(utterance);
        setIsSpeaking(true);
      }
    } else {
      alert('Text-to-speech is not supported in this browser.');
    }
  };

  // Navigate calendar months
  const navigateMonth = (direction) => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCalendarDate(newDate);
  };

  // Update time every second
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Modal handlers
  const openModal = (modalType, data = null) => {
    setActiveModal(modalType);
    setSelectedData(data);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedData(null);
  };

  // Mock notifications
  const notifications = [
    { id: 1, type: 'lease', message: 'Lease renewal due for Downtown Plaza', time: '2 hours ago' },
    { id: 2, type: 'maintenance', message: 'Maintenance request at Riverside Complex', time: '4 hours ago' },
    { id: 3, type: 'payment', message: 'Payment received: $125,000', time: '1 day ago' },
    { id: 4, type: 'deal', message: 'New deal opportunity in Manhattan', time: '2 days ago' },
    { id: 5, type: 'alert', message: 'Property inspection scheduled for Friday', time: '3 days ago' }
  ];

  // Mock tasks
  const tasks = [
    { id: 1, title: 'Review lease agreement for Harbor View', priority: 'high', dueDate: 'Today' },
    { id: 2, title: 'Approve maintenance budget', priority: 'high', dueDate: 'Today' },
    { id: 3, title: 'Meet with new tenants', priority: 'medium', dueDate: 'Tomorrow' },
    { id: 4, title: 'Property inspection - Oak Street', priority: 'medium', dueDate: 'Tomorrow' },
    { id: 5, title: 'Quarterly report preparation', priority: 'low', dueDate: 'This week' },
    { id: 6, title: 'Contract renewal negotiations', priority: 'high', dueDate: 'This week' }
  ];

  // Search handler
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    // In a real app, this would trigger search API calls
    console.log('Searching for:', e.target.value);
  };

  // Mark notification as read
  const markNotificationRead = (id) => {
    console.log('Marking notification as read:', id);
    // In a real app, would update notification status via API
  };

  // Toggle task completion
  const toggleTask = (id) => {
    console.log('Toggling task:', id);
    // In a real app, would update task status via API
  };

  // Portfolio data
  const portfolioData = {
    totalProperties: 47,
    active: 38,
    upcoming: 5,
    completed: 4,
    totalValue: '$245M',
    occupancyRate: 94.5
  };

  // Financial summary data
  const financialData = {
    totalRevenue: '$42.8M',
    remainingBudget: '$8.3M',
    utilizationRate: 83,
    monthlyChange: '+12.4%'
  };

  // Schedule/Timeline data
  const scheduleData = {
    months: 4,
    leaseRenewals: 23,
    percentComplete: 67,
    daysUsed: 245
  };

  // Property status breakdown
  const propertyStatus = [
    { name: 'Fully Leased', value: 32, color: '#10B981' },
    { name: 'Partial Vacancy', value: 8, color: '#F59E0B' },
    { name: 'Under Renovation', value: 5, color: '#8B5CF6' },
    { name: 'New Listings', value: 2, color: '#3B82F6' }
  ];

  // Revenue trends monthly data
  const revenueTrends = [
    { month: 'Jan', revenue: 2.8, expenses: 1.2 },
    { month: 'Feb', revenue: 3.1, expenses: 1.4 },
    { month: 'Mar', revenue: 2.9, expenses: 1.3 },
    { month: 'Apr', revenue: 3.4, expenses: 1.5 },
    { month: 'May', revenue: 3.8, expenses: 1.6 },
    { month: 'Jun', revenue: 3.6, expenses: 1.4 },
    { month: 'Jul', revenue: 4.1, expenses: 1.7 },
    { month: 'Aug', revenue: 3.9, expenses: 1.6 },
    { month: 'Sep', revenue: 4.3, expenses: 1.8 },
    { month: 'Oct', revenue: 4.0, expenses: 1.5 },
    { month: 'Nov', revenue: 4.5, expenses: 1.9 },
    { month: 'Dec', revenue: 4.2, expenses: 1.7 }
  ];

  // Occupancy distribution
  const occupancyData = [
    { category: 'Commercial', percentage: 95, value: 95 },
    { category: 'Residential', percentage: 92, value: 92 },
    { category: 'Mixed-Use', percentage: 88, value: 88 },
    { category: 'Retail', percentage: 91, value: 91 }
  ];

  // Pending changes/issues
  const pendingChanges = {
    amount: '$2.3M',
    count: 8,
    edits: 15
  };

  // Market metrics
  const marketMetrics = [
    { label: 'Avg Rent/SqFt', value: '$42.50', change: '+5.2%', trend: 'up' },
    { label: 'Market Cap Rate', value: '6.8%', change: '-0.3%', trend: 'down' },
    { label: 'Avg Lease Term', value: '5.2 yrs', change: '+0.8', trend: 'up' }
  ];

  // Deal pipeline
  const dealPipeline = [
    { stage: 'Negotiation', count: 12, value: '$18.5M' },
    { stage: 'Due Diligence', count: 8, value: '$12.2M' },
    { stage: 'Closing', count: 5, value: '$8.7M' }
  ];

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6 relative">
      {/* Brightness Overlay - simulates screen brightness */}
      {brightness < 100 && (
        <div 
          className="fixed inset-0 bg-black pointer-events-none z-[9999]"
          style={{ 
            opacity: (100 - brightness) / 100,
            transition: 'opacity 0.2s ease'
          }}
        />
      )}
      
      <div className="max-w-[1800px] mx-auto">
        {/* Top Bar */}
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1">Executive Dashboard</h1>
            <p className="text-gray-400 text-sm md:text-base">Personal Operating System</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search documents, tasks, events"
                value={searchQuery}
                onChange={handleSearch}
                className="bg-black border border-gray-400 rounded-lg pl-10 pr-4 py-2 w-80 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchQuery && (
                <div className="absolute top-full mt-2 w-full bg-black border border-gray-400 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                  <div className="p-3 text-sm text-gray-400">
                    Search results for "{searchQuery}"
                  </div>
                  <div className="border-t border-gray-800">
                    <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors">
                      <div className="font-semibold text-sm">Downtown Plaza Lease</div>
                      <div className="text-xs text-gray-400 mt-1">Document • Updated 2 days ago</div>
                    </button>
                    <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors">
                      <div className="font-semibold text-sm">Q4 Financial Report</div>
                      <div className="text-xs text-gray-400 mt-1">Report • Updated 1 week ago</div>
                    </button>
                    <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors">
                      <div className="font-semibold text-sm">Property Inspection Task</div>
                      <div className="text-xs text-gray-400 mt-1">Task • Due Tomorrow</div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Profile */}
            <div className="relative">
              <button 
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                }}
                className="bg-black border border-gray-400 rounded-lg p-2 hover:bg-gray-900 transition-colors"
              >
                <User size={20} />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-black border border-gray-400 rounded-lg shadow-xl z-50">
                  <div className="p-4 border-b border-gray-800">
                    <div className="font-bold text-sm">{profileName}</div>
                    <div className="text-xs text-gray-400 mt-1">{profileRole}</div>
                    <div className="text-xs text-gray-400">djwoodson@creatrverse.com</div>
                  </div>
                  <div>
                    <button 
                      onClick={() => { setShowUserMenu(false); openProfileModal(); }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm"
                    >
                      My Profile
                    </button>
                    <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm">
                      Settings
                    </button>
                    <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm">
                      Help & Support
                    </button>
                    <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm border-t border-gray-800 text-red-400">
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          
          {/* Top Row: Control Center and Communications Only */}
          {/* Control Center Widget */}
          <div className="col-span-1 md:col-span-6 bg-black border border-gray-400 rounded-xl p-3 md:p-4 text-left relative overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 h-auto md:h-[600px]">
            {/* Top Section: User Info with Real Image */}
            <div className="flex items-center gap-3 mb-3">
              <button 
                onClick={(e) => { e.stopPropagation(); openProfileModal(); }}
                className="relative group"
              >
                <img 
                  src={profileImage} 
                  alt="Profile"
                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-400 group-hover:border-cyan-400 transition-colors"
                />
                <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
              </button>
              <div>
                <div className="font-bold text-base">{profileName}</div>
                <div className="text-xs text-gray-400">{profileRole}</div>
              </div>
            </div>

            {/* Date, Time & Weather Row */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button 
                onClick={(e) => { e.stopPropagation(); openModal('time-schedule'); }}
                className="bg-gray-950 rounded-lg p-5 shadow-lg hover:bg-gray-900 transition-all text-left"
              >
                <div className="text-base text-gray-300 mb-1 font-semibold">
                  {currentTime.toLocaleDateString('en-US', { weekday: 'long' })}
                </div>
                <div className="text-3xl font-bold leading-tight">
                  {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                </div>
                <div className="text-base text-gray-400 mt-1">
                  {currentTime.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </button>
              
              {/* Weather Card with Animated Background - Taller */}
              <button 
                onClick={(e) => { e.stopPropagation(); openModal('weather'); }}
                className="relative rounded-lg p-4 shadow-lg overflow-hidden transition-all text-left group"
                style={{
                  background: weatherData ? getWeatherBackground(weatherData.current?.weather_code, weatherData.current?.temperature_2m) : 'linear-gradient(135deg, #5B9BDF 0%, #4A8FD8 50%, #3B7AC7 100%)',
                }}
              >
                {/* Animated Cloud Background */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-1 right-2 w-10 h-6 bg-white rounded-full blur-sm animate-pulse"></div>
                  <div className="absolute top-4 right-6 w-8 h-5 bg-white rounded-full blur-sm animate-pulse" style={{ animationDelay: '1s' }}></div>
                  <div className="absolute bottom-2 left-2 w-12 h-7 bg-white rounded-full blur-md animate-pulse" style={{ animationDelay: '2s' }}></div>
                </div>
                
                {/* Weather Icon */}
                <div className="absolute top-2 right-2">
                  {weatherLoading ? (
                    <Loader2 className="w-16 h-16 text-white animate-spin" />
                  ) : (
                    getWeatherIcon(weatherData?.current?.weather_code || 2, 'w-16 h-16')
                  )}
                </div>
                
                <div className="relative z-10" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.3)' }}>
                  {weatherLoading ? (
                    <>
                      <div className="text-4xl font-bold text-white mb-0.5">--°</div>
                      <div className="text-xs text-white/90 mb-2">Loading...</div>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl font-bold text-white mb-0.5">
                        {Math.round(weatherData?.current?.temperature_2m || 0)}°
                      </div>
                      <div className="text-xs text-white/90 mb-1">
                        {getWeatherDescription(weatherData?.current?.weather_code || 0)}
                      </div>
                      {weatherLocation && (
                        <div className="text-[10px] text-white/70 mb-2">{weatherLocation}</div>
                      )}
                    </>
                  )}
                  
                  {/* 5-Day Forecast - Bigger */}
                  <div className="pt-2 border-t border-gray-400/30">
                    <div className="grid grid-cols-5 gap-1 text-center">
                      {weatherData?.daily?.time?.slice(0, 5).map((day, idx) => {
                        // Parse date correctly - add T00:00:00 to avoid UTC timezone issues
                        const dateObj = new Date(day + 'T00:00:00');
                        const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                        const weatherCode = weatherData.daily.weather_code?.[idx] || 0;
                        const maxTemp = Math.round(weatherData.daily.temperature_2m_max?.[idx] || 0);
                        return (
                          <div key={idx}>
                            <div className="text-xs text-white/90 mb-1 font-medium">{dayName}</div>
                            {getWeatherIcon(weatherCode, 'w-7 h-7 mx-auto mb-1')}
                            <div className="text-xs text-white font-semibold">{maxTemp}°</div>
                          </div>
                        );
                      }) || (
                        // Fallback while loading
                        ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, idx) => (
                          <div key={idx}>
                            <div className="text-xs text-white/90 mb-1 font-medium">{day}</div>
                            <div className="w-7 h-7 mx-auto mb-1 bg-white/20 rounded animate-pulse"></div>
                            <div className="text-xs text-white font-semibold">--°</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Small Square Cards - Calculator, Record, Alarm, Notes, Screen Capture - Taller Cards */}
            <div className="grid grid-cols-5 gap-2 mb-2">
              {/* Calculator */}
              <button 
                onClick={(e) => { e.stopPropagation(); openModal('calculator'); }}
                className="bg-gray-950 rounded-lg p-2 shadow-lg hover:bg-gray-900 transition-all flex flex-col items-center justify-center"
                style={{ height: '75px' }}
              >
                <div className="w-11 h-11 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="5" y="5" width="3" height="3" rx="0.5"/>
                    <rect x="10" y="5" width="3" height="3" rx="0.5"/>
                    <rect x="15" y="5" width="3" height="3" rx="0.5"/>
                    <rect x="5" y="10" width="3" height="3" rx="0.5"/>
                    <rect x="10" y="10" width="3" height="3" rx="0.5"/>
                    <rect x="15" y="10" width="3" height="3" rx="0.5"/>
                    <rect x="5" y="15" width="3" height="3" rx="0.5"/>
                    <rect x="10" y="15" width="3" height="3" rx="0.5"/>
                    <rect x="15" y="15" width="8" height="3" rx="0.5"/>
                  </svg>
                </div>
                <span className="text-[10px] text-gray-400 font-medium mt-1">Calculator</span>
              </button>

              {/* Voice Recorder */}
              <button 
                onClick={(e) => { e.stopPropagation(); openModal('voice-recorder'); }}
                className="bg-gray-950 rounded-lg p-2 shadow-lg hover:bg-gray-900 transition-all flex flex-col items-center justify-center"
                style={{ height: '75px' }}
              >
                <div className="w-11 h-11 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center shadow-lg relative">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="9" y="3" width="6" height="11" rx="3"/>
                    <path d="M6 10v2a6 6 0 0012 0v-2" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <line x1="12" y1="18" x2="12" y2="21" stroke="currentColor" strokeWidth="2"/>
                    <line x1="9" y1="21" x2="15" y2="21" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                </div>
                <span className="text-[10px] text-gray-400 font-medium mt-1">Record</span>
              </button>

              {/* Alarm */}
              <button 
                onClick={(e) => { e.stopPropagation(); openModal('alarm'); }}
                className="bg-gray-950 rounded-lg p-2 shadow-lg hover:bg-gray-900 transition-all flex flex-col items-center justify-center"
                style={{ height: '75px' }}
              >
                <div className="w-11 h-11 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="13" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <path d="M12 9v4l3 2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                    <path d="M4 4L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M20 4L22 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="text-[10px] text-gray-400 font-medium mt-1">Alarm</span>
              </button>

              {/* Notes */}
              <button 
                onClick={(e) => { e.stopPropagation(); openModal('notes'); }}
                className="bg-gray-950 rounded-lg p-2 shadow-lg hover:bg-gray-900 transition-all flex flex-col items-center justify-center"
                style={{ height: '75px' }}
              >
                <div className="w-11 h-11 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M7 3h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z"/>
                    <line x1="9" y1="7" x2="15" y2="7" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="9" y1="11" x2="15" y2="11" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="9" y1="15" x2="12" y2="15" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="text-[10px] text-gray-400 font-medium mt-1">Notes</span>
              </button>

              {/* Screen Capture */}
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (isScreenRecording) {
                    stopScreenRecording();
                  } else {
                    startScreenRecording();
                  }
                }}
                className={`bg-gray-950 rounded-lg p-2 shadow-lg hover:bg-gray-900 transition-all flex flex-col items-center justify-center ${isScreenRecording ? 'ring-2 ring-red-500 animate-pulse' : ''}`}
                style={{ height: '75px' }}
              >
                <div className={`w-11 h-11 ${isScreenRecording ? 'bg-gradient-to-br from-red-500 to-red-700' : 'bg-gradient-to-br from-pink-500 to-rose-600'} rounded-xl flex items-center justify-center shadow-lg relative`}>
                  {isScreenRecording ? (
                    <>
                      <div className="w-5 h-5 bg-white rounded-sm"></div>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                    </>
                  ) : (
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="4" width="18" height="14" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="11" r="3" fill="currentColor"/>
                      <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="12" y1="18" x2="12" y2="21" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </div>
                <span className="text-[10px] text-gray-400 font-medium mt-1 text-center leading-tight">
                  {isScreenRecording ? formatRecordingTime(screenRecordingTime) : 'Capture'}
                </span>
              </button>
            </div>

            {/* Brightness Slider */}
            <div className="w-full bg-gray-950 rounded-lg p-2 mb-2 shadow-lg text-left">
              <div className="flex items-center gap-3">
                <Sun size={14} className="text-gray-400" />
                <div className="flex-1 relative">
                  <input 
                    type="range"
                    min="20"
                    max="100"
                    value={brightness}
                    onChange={(e) => {
                      e.stopPropagation();
                      setBrightness(parseInt(e.target.value));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full h-2 bg-gray-700 rounded-full appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, white ${brightness}%, rgb(55, 65, 81) ${brightness}%)`
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-8 text-right">{brightness}%</span>
              </div>
            </div>

            {/* Volume Slider */}
            <div className="w-full bg-gray-950 rounded-lg p-2 mb-2 shadow-lg text-left">
              <div className="flex items-center gap-3">
                <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                </svg>
                <div className="flex-1 relative">
                  <input 
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    onChange={(e) => {
                      e.stopPropagation();
                      const newVolume = parseInt(e.target.value);
                      setVolume(newVolume);
                      // Control all audio/video elements on the page
                      document.querySelectorAll('audio, video').forEach(el => {
                        el.volume = newVolume / 100;
                      });
                      // Also update Spotify volume if connected
                      if (spotifyToken && spotifyConnected) {
                        spotifySetVolume(newVolume);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full h-2 bg-gray-700 rounded-full appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, white ${volume}%, rgb(55, 65, 81) ${volume}%)`
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-8 text-right">{volume}%</span>
              </div>
            </div>

            {/* Music Player Widget */}
            <div className="w-full bg-gray-950 rounded-lg p-2 shadow-lg">
              {/* Player Content */}
              <button 
                onClick={(e) => { e.stopPropagation(); openModal('music-player'); }}
                className="w-full hover:bg-gray-900 rounded-lg p-1 transition-all text-left"
              >
                <div className="flex items-center gap-3 mb-1">
                  <img 
                    src={currentTrack?.album_art || "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=100&h=100&fit=crop"}
                    alt="Album Cover"
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div className="flex-1 overflow-hidden">
                    <div className="font-semibold text-sm truncate">
                      {currentTrack?.name || 'No track playing'}
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      {currentTrack?.artist || 'Connect to Spotify to play music'}
                    </div>
                  </div>
                  {/* Spotify Connect/Status - On the player bar */}
                  {!spotifyConnected ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); connectToSpotify(); }}
                      disabled={spotifyLoading}
                      className="px-2 py-1 bg-green-600 hover:bg-green-700 disabled:bg-green-800 rounded flex items-center gap-1 text-white text-xs font-medium transition-colors flex-shrink-0"
                    >
                      {spotifyLoading ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                        </svg>
                      )}
                    </button>
                  ) : (
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 flex-shrink-0"
                    >
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                      <button
                        onClick={disconnectSpotify}
                        className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                        title="Disconnect Spotify"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Progress bar with controls inline */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">
                    {spotifyProgress ? `${Math.floor(spotifyProgress / 60000)}:${String(Math.floor((spotifyProgress % 60000) / 1000)).padStart(2, '0')}` : '0:00'}
                  </span>
                  <div className="flex-1 bg-gray-700 rounded-full h-1">
                    <div 
                      className="bg-green-500 rounded-full h-1 transition-all" 
                      style={{ width: currentTrack?.duration ? `${(spotifyProgress / currentTrack.duration) * 100}%` : '0%' }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400">
                    {currentTrack?.duration ? `${Math.floor(currentTrack.duration / 60000)}:${String(Math.floor((currentTrack.duration % 60000) / 1000)).padStart(2, '0')}` : '0:00'}
                  </span>
                  
                  {/* Play controls inline */}
                  <div className="flex items-center gap-2 ml-2">
                    <button 
                      className="hover:text-green-400 transition-colors" 
                      onClick={(e) => {
                        e.stopPropagation();
                        spotifyPrev();
                      }}
                      disabled={!spotifyConnected}
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                      </svg>
                    </button>
                    <button 
                      className={`w-9 h-9 ${spotifyConnected ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-600'} rounded-full flex items-center justify-center transition-colors`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (spotifyConnected) {
                          spotifyPlayPause();
                        } else {
                          connectToSpotify();
                        }
                      }}
                    >
                      {isPlaying ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      )}
                    </button>
                    <button 
                      className="hover:text-green-400 transition-colors" 
                      onClick={(e) => {
                        e.stopPropagation();
                        spotifyNext();
                      }}
                      disabled={!spotifyConnected}
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* AI Assistant Widget */}
          <div className="col-span-1 md:col-span-6 bg-black border border-gray-400 rounded-xl p-3 md:p-4 text-left flex flex-col hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 relative h-[500px] md:h-[600px]">
            {/* Header with action buttons */}
            <div className="flex flex-col gap-2 mb-3">
              {/* Title */}
              <h2 className="text-base md:text-lg font-bold flex items-center gap-2">
                <Bot size={18} className="text-white" />
                AI Assistant
              </h2>
              
              {/* Single row: All buttons beneath title */}
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
                {/* Model Tabs */}
                <div className="flex items-center gap-1 bg-[#080b12] rounded-lg p-1 flex-shrink-0">
                  <button
                    onClick={() => setAiModel('gpt')}
                    className={`px-2 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                      aiModel === 'gpt' 
                        ? 'bg-white text-black' 
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    Chat GPT
                  </button>
                  <button
                    onClick={() => setAiModel('gemini')}
                    className={`px-2 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                      aiModel === 'gemini' 
                        ? 'bg-white text-black' 
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    Gemini
                  </button>
                  <button
                    onClick={() => setAiModel('perplexity')}
                    className={`px-2 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                      aiModel === 'perplexity' 
                        ? 'bg-white text-black' 
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    Perplexity
                  </button>
                  <button
                    onClick={() => setAiModel('claude')}
                    className={`px-2 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                      aiModel === 'claude' 
                        ? 'bg-white text-black' 
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    Claude
                  </button>
                </div>
                
                {/* Action Buttons */}
                <button 
                  onClick={() => setShowVaultModal(true)}
                  className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-colors text-xs flex-shrink-0"
                  title="View saved conversations"
                >
                  <Vault size={14} />
                  <span>{aiVaultCount}</span>
                </button>
                <button 
                  onClick={saveAiConversation}
                  className="flex items-center gap-1 px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors text-xs whitespace-nowrap flex-shrink-0"
                  title="Save conversation"
                >
                  <Save size={14} />
                  <span>Save</span>
                </button>
                <button 
                  onClick={clearAiChat}
                  className="flex items-center gap-1 px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors text-xs whitespace-nowrap flex-shrink-0"
                  title="Clear chat"
                >
                  <X size={14} />
                  <span>Clear</span>
                </button>
              </div>
            </div>
            
            {/* Vault Modal */}
            {showVaultModal && (
              <div className="absolute inset-0 bg-black/90 z-50 rounded-xl flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-gray-700">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Vault size={20} className="text-white" />
                    Saved Conversations ({savedAiConversations.length})
                  </h3>
                  <button 
                    onClick={() => setShowVaultModal(false)}
                    className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {savedAiConversations.length === 0 ? (
                    <div className="text-center py-8">
                      <Vault size={40} className="mx-auto text-gray-600 mb-3" />
                      <p className="text-gray-400">No saved conversations yet</p>
                      <p className="text-sm text-gray-500 mt-1">Click "Save" to save your current conversation</p>
                    </div>
                  ) : (
                    savedAiConversations.map((convo) => (
                      <div 
                        key={convo.id}
                        className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 hover:border-purple-500/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-white truncate">{convo.title}</h4>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                              <span>{convo.messageCount || convo.messages.length} messages</span>
                              <span>•</span>
                              <span>{new Date(convo.savedAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => loadConversationFromVault(convo)}
                              className="px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-white rounded-lg text-sm transition-colors"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => deleteConversationFromVault(convo.id)}
                              className="p-1.5 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            
            {/* Chat Messages */}
            <div 
              ref={aiChatRef}
              className="flex-1 overflow-y-auto space-y-3 mb-3 pr-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
              style={{ maxHeight: 'calc(100% - 160px)' }}
            >
              {aiMessages.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    msg.role === 'user' 
                      ? 'bg-[#1e2430] text-white rounded-br-md' 
                      : 'bg-[#080b12] text-gray-100 rounded-bl-md'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <Bot size={12} className="text-white" />
                        <span className="text-[10px] text-white font-medium">AI Assistant</span>
                      </div>
                    )}
                    {msg.role === 'assistant' ? (
                      <div className="text-sm leading-relaxed ai-message-content">
                        {msg.content.split('\n').map((line, lineIdx) => {
                          // Check for headers
                          if (line.startsWith('### ')) {
                            return <h4 key={lineIdx} className="font-bold text-white text-sm mt-2 mb-1">{line.replace('### ', '')}</h4>;
                          }
                          if (line.startsWith('## ')) {
                            return <h3 key={lineIdx} className="font-bold text-white text-base mt-3 mb-1">{line.replace('## ', '')}</h3>;
                          }
                          if (line.startsWith('# ')) {
                            return <h2 key={lineIdx} className="font-bold text-white text-lg mt-3 mb-2">{line.replace('# ', '')}</h2>;
                          }
                          
                          // Check for bullet points
                          if (line.match(/^[\s]*[-•*]\s/)) {
                            const indent = line.match(/^(\s*)/)[1].length;
                            const content = line.replace(/^[\s]*[-•*]\s/, '');
                            return (
                              <div key={lineIdx} className="flex items-start gap-2 my-0.5" style={{ marginLeft: `${indent * 4}px` }}>
                                <span className="text-white mt-0.5">•</span>
                                <span dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(content) }} />
                              </div>
                            );
                          }
                          
                          // Check for numbered lists
                          if (line.match(/^[\s]*\d+\.\s/)) {
                            const indent = line.match(/^(\s*)/)[1].length;
                            const match = line.match(/^[\s]*(\d+)\.\s(.*)$/);
                            if (match) {
                              return (
                                <div key={lineIdx} className="flex items-start gap-2 my-0.5" style={{ marginLeft: `${indent * 4}px` }}>
                                  <span className="text-white font-semibold min-w-[20px]">{match[1]}.</span>
                                  <span dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(match[2]) }} />
                                </div>
                              );
                            }
                          }
                          
                          // Check for code blocks
                          if (line.startsWith('```')) {
                            return null; // Skip code fence markers
                          }
                          
                          // Empty line = paragraph break
                          if (line.trim() === '') {
                            return <div key={lineIdx} className="h-2" />;
                          }
                          
                          // Regular paragraph with inline formatting
                          return (
                            <p key={lineIdx} className="my-0.5" dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(line) }} />
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}
              {aiLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#080b12] rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin text-white" />
                      <span className="text-sm text-gray-400">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Bottom Toolbar */}
            <div className="flex items-center gap-2 mb-2 px-1 relative">
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileAttachment}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
              />
              
              {/* Attachment button */}
              <button 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAttachment}
                className={`p-2 hover:bg-gray-800 rounded-lg transition-colors ${attachedFile ? 'text-white' : 'text-gray-400 hover:text-white'} ${uploadingAttachment ? 'opacity-50' : ''}`} 
                title={attachedFile ? `Attached: ${attachedFile.name}` : 'Attach file'}
              >
                {uploadingAttachment ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
              </button>
              
              {/* Emoji button */}
              <div className="relative">
                <button 
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className={`p-2 hover:bg-gray-800 rounded-lg transition-colors ${showEmojiPicker ? 'text-yellow-400' : 'text-gray-400 hover:text-white'}`} 
                  title="Add emoji"
                >
                  <Smile size={18} />
                </button>
                
                {/* Emoji Picker Dropdown - Fixed Grid Layout */}
                {showEmojiPicker && (
                  <div className="absolute bottom-full left-0 mb-2 bg-gray-900 border border-gray-700 rounded-xl p-3 shadow-2xl z-50 min-w-[200px]">
                    <div className="text-xs text-gray-400 mb-2 font-medium">Quick Emojis</div>
                    <div className="space-y-1">
                      {commonEmojis.map((row, rowIdx) => (
                        <div key={rowIdx} className="flex gap-1">
                          {row.map((emoji, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleEmojiSelect(emoji)}
                              className="w-9 h-9 text-xl hover:bg-gray-800 rounded-lg transition-colors flex items-center justify-center"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Voice recording button */}
              <button 
                onClick={toggleVoiceRecording}
                className={`p-2 hover:bg-gray-800 rounded-lg transition-colors ${isRecording ? 'text-red-500 animate-pulse' : 'text-gray-400 hover:text-white'}`} 
                title={isRecording ? 'Stop recording' : 'Voice input'}
              >
                <Mic size={18} />
              </button>
              
              {/* Text-to-speech button */}
              <button 
                onClick={speakLastResponse}
                className={`p-2 hover:bg-gray-800 rounded-lg transition-colors ${isSpeaking ? 'text-green-400' : 'text-gray-400 hover:text-white'}`} 
                title={isSpeaking ? 'Stop speaking' : 'Read last response'}
              >
                <Volume2 size={18} />
              </button>
              
              {/* Recording indicator */}
              {isRecording && (
                <span className="text-xs text-red-400 animate-pulse ml-1">● Recording...</span>
              )}
            </div>
            
            {/* Attachment Preview Popup - Separate from input */}
            {attachedFile && (
              <div className="mb-2 mx-1 p-3 bg-gray-800/80 border border-gray-700 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <Paperclip size={20} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white truncate max-w-[180px]">{attachedFile.name}</div>
                      <div className="text-xs text-gray-400">
                        {uploadingAttachment ? (
                          <span className="text-yellow-400">Uploading...</span>
                        ) : attachmentUploaded ? (
                          <span className="text-green-400">✓ Ready to send</span>
                        ) : (
                          <span>{(attachedFile.size / 1024).toFixed(1)} KB</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={removeAttachment}
                    className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                    title="Remove attachment"
                  >
                    <X size={16} />
                  </button>
                </div>
                {attachmentUploaded && (
                  <div className="mt-2 text-xs text-gray-400">
                    💡 Tip: Say "send to documents" to save this file to your Documents page
                  </div>
                )}
              </div>
            )}
            
            {/* Input Area - Taller */}
            <div className="flex gap-2">
              <input
                type="text"
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendAiMessage()}
                placeholder={attachedFile ? "Add a message or say 'send to documents'..." : "Type a message..."}
                className="flex-1 bg-[#080b12] border-0 rounded-xl px-4 py-4 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors"
                disabled={aiLoading}
              />
              <button
                onClick={sendAiMessage}
                disabled={aiLoading || (!aiInput.trim() && !attachmentUploaded)}
                className="px-5 py-4 bg-[#080b12] hover:bg-[#0f1219] disabled:bg-[#080b12] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all flex items-center gap-2 text-white hover:text-white"
              >
                <Send size={18} />
              </button>
            </div>
          </div>

        </div>

        {/* Bottom 5 Containers - Real Estate Talent Agency Style */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4 mt-4">
          
          {/* Today's Calendar Container */}
          <div className="col-span-1 sm:col-span-1 md:col-span-3 bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Calendar size={20} className="text-white" />
                  Today's Schedule
                </h3>
                <p className="text-xs text-gray-400 mt-1 ml-7">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              <span className="text-xs text-gray-400">
                {todayOutlookEvents.filter(e => completedEventIds.includes(e.id) || isEventPast(e.time)).length}/{todayOutlookEvents.length} done
              </span>
            </div>
            
            {outlookEventsLoading ? (
              <div className="text-center text-gray-500 text-sm py-8">
                <Loader2 size={20} className="animate-spin inline mr-2" />
                Loading calendar...
              </div>
            ) : todayOutlookEvents.length === 0 ? (
              <div className="text-center py-8">
                <Calendar size={40} className="mx-auto text-gray-600 mb-3" />
                <p className="text-gray-500 text-sm">No events scheduled for today</p>
                <p className="text-gray-600 text-xs mt-1">Sync your Outlook calendar to see events</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                {todayOutlookEvents.map((event, idx) => {
                  const eventId = event.id || `event-${idx}`;
                  const isPast = isEventPast(event.time);
                  const isCompleted = completedEventIds.includes(eventId) || isPast;
                  
                  return (
                    <div 
                      key={eventId}
                      onClick={() => toggleEventComplete(eventId)}
                      className={`flex items-start gap-3 p-3 rounded-lg transition-all cursor-pointer ${
                        isCompleted 
                          ? 'bg-[#080b12] opacity-60' 
                          : 'bg-[#080b12] hover:bg-[#0f1219]'
                      }`}
                    >
                      {/* Checkbox */}
                      <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-all ${
                        isCompleted 
                          ? 'bg-green-500 border-green-500' 
                          : 'border-white/40 hover:border-white/60'
                      }`}>
                        {isCompleted && (
                          <CheckCircle size={14} className="text-white" />
                        )}
                      </div>
                      
                      {/* Time */}
                      <div className="flex-shrink-0 w-14 text-center">
                        <span className={`text-sm font-bold ${isCompleted ? 'text-gray-400' : 'text-white'}`}>
                          {(event.time && event.time !== 'missing value') ? event.time : '—'}
                        </span>
                      </div>
                      
                      {/* Event Content */}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium leading-tight ${
                          isCompleted ? 'text-gray-400 line-through' : 'text-white'
                        }`}>
                          {(event.subject && event.subject !== 'missing value') 
                            ? event.subject 
                            : (event.title && event.title !== 'missing value' ? event.title : 'Untitled Event')}
                        </div>
                        {event.location && event.location !== 'missing value' && (
                          <div className={`text-xs mt-1 flex items-center gap-1 ${
                            isCompleted ? 'text-gray-500' : 'text-gray-300'
                          }`}>
                            <MapPin size={10} />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Status indicator */}
                      {isPast && !completedEventIds.includes(eventId) && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">
                          Past
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="col-span-1 sm:col-span-1 md:col-span-3 bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <FileText size={18} className="text-white" />
                Documents
              </h3>
              <button 
                onClick={() => navigate('/documents-re')}
                className="text-xs text-white hover:text-white transition-colors"
              >
                View All
              </button>
            </div>
            
            {/* Documents List - 4 rows visible */}
            <div className="space-y-2 flex-1">
              {documentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : recentDocuments.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No documents yet</p>
                </div>
              ) : (
                recentDocuments.slice(0, 4).map((doc) => {
                  const fileInfo = getFileIcon(doc.file_type);
                  const dateStr = doc.date || doc.created_at?.slice(0, 10) || '';
                  const formattedDate = dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                  
                  return (
                    <div 
                      key={doc.id}
                      onClick={() => setSelectedDocument(doc)}
                      className="flex items-center gap-3 p-2 bg-gray-900/50 rounded-lg hover:bg-gray-900 transition-colors cursor-pointer"
                    >
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-xl ${fileInfo.color}`}>
                        {fileInfo.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{doc.name || doc.original_filename}</div>
                        <div className="text-xs text-gray-400 flex items-center gap-2">
                          <span>{doc.size || '—'}</span>
                          {formattedDate && (
                            <>
                              <span>•</span>
                              <span>{formattedDate}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            {/* Add Document Button */}
            <div className="mt-3 pt-3 border-t border-gray-700">
              <input
                type="file"
                ref={documentFileInputRef}
                onChange={handleDashboardDocumentUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
              />
              <button
                onClick={() => documentFileInputRef.current?.click()}
                disabled={documentUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-800 disabled:opacity-50 rounded-lg text-sm font-medium text-white transition-colors"
              >
                {documentUploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Add Document
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Latest Content (replaces Top Property This Month) */}
          <div className="col-span-1 sm:col-span-2 md:col-span-3 bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Film size={20} className="text-white" />
                Latest Content
              </h3>
              <div className="flex gap-2">
                <button 
                  onClick={prevVideo}
                  disabled={latestVideos.length <= 1}
                  className="p-1 hover:bg-gray-800 rounded disabled:opacity-30 text-gray-400"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button 
                  onClick={nextVideo}
                  disabled={latestVideos.length <= 1}
                  className="p-1 hover:bg-gray-800 rounded disabled:opacity-30 text-gray-400"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
            
            {latestVideosLoading ? (
              <div className="flex items-center justify-center flex-1">
                <Loader2 size={24} className="animate-spin text-white" />
              </div>
            ) : latestVideos.length > 0 ? (
              <div className="flex flex-col flex-1">
                {/* Large Thumbnail with Category Badge */}
                <div 
                  className="relative w-full aspect-video rounded-lg overflow-hidden cursor-pointer group mb-4"
                  onClick={() => navigate('/media-portfolio-re')}
                >
                  <img 
                    src={getVideoThumbnail(latestVideos[currentVideoIndex])} 
                    alt={latestVideos[currentVideoIndex].name}
                    className="w-full h-full object-cover"
                  />
                  {/* Category Badge */}
                  <div className="absolute top-3 left-3">
                    <span className="bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded">
                      {latestVideos[currentVideoIndex].category || 'Highlights'}
                    </span>
                  </div>
                  {/* Play overlay on hover */}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play size={40} className="text-white" />
                  </div>
                  {/* YouTube indicator */}
                  {latestVideos[currentVideoIndex].is_youtube && (
                    <div className="absolute bottom-2 right-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded font-semibold">YT</div>
                  )}
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-4 mt-auto">
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Views</div>
                    <div className="text-2xl font-bold flex items-center gap-2">
                      <Eye size={18} className="text-gray-500" />
                      {latestVideos[currentVideoIndex].views || '0'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Total Videos</div>
                    <div className="text-2xl font-bold">{latestVideos.length}</div>
                    <div className="text-sm text-purple-400">in library</div>
                  </div>
                </div>
                
                {/* Pagination Dots */}
                {latestVideos.length > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    {latestVideos.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentVideoIndex(idx)}
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === currentVideoIndex ? 'bg-purple-400' : 'bg-gray-600 hover:bg-gray-500'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 flex-1 flex flex-col items-center justify-center">
                <Film size={40} className="mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400 mb-2">No videos yet</p>
                <button 
                  onClick={() => navigate('/media-portfolio-re')}
                  className="text-sm text-purple-400 hover:text-purple-300"
                >
                  Add your first video →
                </button>
              </div>
            )}
          </div>

          {/* Upcoming Flights (replaces Leases This Week) */}
          <div className="col-span-1 sm:col-span-1 md:col-span-3 bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Plane size={18} className="text-white" />
                Flights
              </h3>
              <button 
                onClick={() => navigate('/travel')}
                className="text-xs text-white hover:text-white"
              >
                View All
              </button>
            </div>
            
            {flightsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={24} className="animate-spin text-white" />
              </div>
            ) : upcomingFlights.length > 0 ? (
              <div className="space-y-3">
                {upcomingFlights.map((flight, idx) => (
                  <div key={flight.id || idx} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <Plane size={18} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-white truncate">{flight.destination}</div>
                      <div className="text-xs text-gray-400 truncate">{flight.airline || flight.flight_details?.split('•')[0] || 'Flight'}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-white">{formatFlightDate(flight.start_date)}</div>
                      <div className="text-xs text-gray-500">{flight.departure_time || ''}</div>
                    </div>
                  </div>
                ))}
                
                {/* Next flight countdown */}
                {upcomingFlights[0] && (
                  <div className="mt-4 pt-3 border-t border-gray-800">
                    <div className="text-xs text-gray-400 mb-1">Next flight in</div>
                    <div className="text-2xl font-bold text-white">
                      {Math.max(0, Math.ceil((new Date(upcomingFlights[0].start_date) - new Date()) / (1000 * 60 * 60 * 24)))} days
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <Plane size={32} className="mx-auto text-gray-600 mb-2" />
                <p className="text-gray-400 text-sm mb-1">No upcoming flights</p>
                <p className="text-xs text-gray-500">Your travel itinerary is clear</p>
              </div>
            )}
          </div>

          {/* Meetings (replaces Active Deal Pipeline) */}
          <div className="col-span-1 sm:col-span-2 md:col-span-7 bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <CalendarClock size={18} className="text-white" />
                Meetings
              </h3>
            </div>

            {/* Status Filter Tabs */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              {[
                { id: 'all', label: 'All', color: 'bg-gray-600' },
                { id: 'upcoming', label: 'Upcoming', color: 'bg-blue-600' },
                { id: 'in_progress', label: 'In Progress', color: 'bg-yellow-600' },
                { id: 'done', label: 'Done', color: 'bg-green-600' }
              ].map(status => {
                const count = status.id === 'all' 
                  ? upcomingMeetings.length 
                  : upcomingMeetings.filter(m => getDashboardMeetingStatus(m) === status.id).length;
                
                return (
                  <button 
                    key={status.id}
                    onClick={() => setMeetingsStatusFilter(status.id)}
                    className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[10px] md:text-xs font-semibold transition-colors flex items-center gap-1 md:gap-1.5 whitespace-nowrap ${
                      meetingsStatusFilter === status.id 
                        ? `${status.color} text-white` 
                        : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                    }`}
                  >
                    {status.label}
                    <span className={`px-1 md:px-1.5 py-0.5 rounded-full text-[9px] md:text-[10px] ${
                      meetingsStatusFilter === status.id ? 'bg-white/20' : 'bg-gray-700'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Meetings Table - Fixed height for 5 rows with scroll */}
            {meetingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-white" />
              </div>
            ) : filteredMeetings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[500px] table-fixed">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-800">
                      <th className="pb-3 w-[35%]">Meeting</th>
                      <th className="pb-3 w-[25%]">Company</th>
                      <th className="pb-3 w-[20%]">Date & Time</th>
                      <th className="pb-3 w-[20%]">Type</th>
                    </tr>
                  </thead>
                </table>
                {/* Scrollable body - max height for 5 rows (approx 60px per row) */}
                <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
                  <table className="w-full min-w-[500px] table-fixed">
                    <tbody>
                      {filteredMeetings.slice(0, 5).map((meeting, idx) => {
                        // Use date field directly if datetime doesn't work
                        const dateStr = meeting.date || '';
                        const timeStr = meeting.time || '';
                        let displayDate = 'TBD';
                        let displayTime = 'TBD';
                        
                        if (dateStr && dateStr !== 'TBD' && dateStr !== '-') {
                          const cleanDate = String(dateStr).split(' ')[0];
                          const dateObj = new Date(cleanDate + 'T12:00:00');
                          if (!isNaN(dateObj.getTime())) {
                            displayDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          }
                        }
                        
                        if (timeStr && timeStr !== 'TBD' && timeStr !== '-') {
                          // Handle Excel decimal time format
                          if (!isNaN(parseFloat(timeStr))) {
                            const decimal = parseFloat(timeStr);
                            const hours = Math.floor(decimal * 24);
                            const minutes = Math.round((decimal * 24 - hours) * 60);
                            const ampm = hours >= 12 ? 'PM' : 'AM';
                            const hour12 = hours % 12 || 12;
                            displayTime = `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
                          } else {
                            displayTime = timeStr;
                          }
                        }
                        
                        // Get company from organization field or from participant's company
                        const companyName = meeting.organization || meeting.company || meeting.participants?.[0]?.company || '-';
                        const meetingType = meeting.category || meeting.type || 'Personal';
                        return (
                          <tr 
                            key={meeting.id || idx} 
                            className="border-b border-gray-800 hover:bg-gray-900 cursor-pointer"
                            onClick={() => setSelectedDashboardMeeting(meeting)}
                          >
                            <td className="py-3 w-[35%]">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full ${getMeetingTypeColor(meetingType)} flex items-center justify-center text-sm font-bold text-white flex-shrink-0`}>
                                  {(meeting.title || meeting.name || 'M')[0].toUpperCase()}
                                </div>
                                <span className="truncate">{meeting.title || meeting.name || 'Meeting'}</span>
                              </div>
                            </td>
                            <td className="py-3 w-[25%] text-gray-300 truncate">{companyName}</td>
                            <td className="py-3 w-[20%]">
                              <div className="text-white font-semibold">{displayDate}</div>
                              <div className="text-xs text-gray-500">{displayTime}</div>
                            </td>
                            <td className="py-3 w-[20%]">
                              <span className={`px-3 py-1 ${getMeetingTypeColor(meetingType)} text-white rounded text-xs font-semibold`}>
                                {meetingType}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarClock size={40} className="mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400 mb-1">No upcoming meetings</p>
                <p className="text-xs text-gray-500">Your calendar is clear</p>
              </div>
            )}
          </div>

          {/* Calendar Widget (replaces This Week's Inspections) */}
          <div className="col-span-1 sm:col-span-2 md:col-span-5 bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 overflow-visible">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Calendar size={18} className="text-white" />
                Calendar
              </h3>
              <div className="flex items-center gap-2">
                <button onClick={prevMonth} className="p-1 hover:bg-gray-800 rounded border border-gray-600">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm font-semibold text-white min-w-[140px] text-center">{getMonthName(calendarDate)}</span>
                <button onClick={nextMonth} className="p-1 hover:bg-gray-800 rounded border border-gray-600">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Today indicator - right under title */}
            <div className="mb-4">
              <span className="text-xs text-gray-400">Today: </span>
              <span className="text-xs text-white font-semibold">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>

            {/* Calendar Grid */}
            <div className="border border-white rounded-lg overflow-visible">
              {/* Day Headers */}
              <div className="grid grid-cols-7 border-b border-white">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-400 py-3 border-r border-white last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {/* Empty cells for days before the 1st */}
                {Array.from({ length: getFirstDayOfMonth(calendarDate) }).map((_, idx) => (
                  <div key={`empty-${idx}`} className="h-14 border-r border-b border-white last:border-r-0 bg-black"></div>
                ))}
                
                {/* Days of the month */}
                {Array.from({ length: getDaysInMonth(calendarDate) }).map((_, idx) => {
                  const day = idx + 1;
                  const dayIndex = (getFirstDayOfMonth(calendarDate) + idx) % 7;
                  const isLastCol = dayIndex === 6;
                  const rowIndex = Math.floor((getFirstDayOfMonth(calendarDate) + idx) / 7);
                  const totalRows = Math.ceil((getDaysInMonth(calendarDate) + getFirstDayOfMonth(calendarDate)) / 7);
                  const isLastRow = rowIndex === totalRows - 1;
                  const isTopHalf = rowIndex < 2; // First 2 rows show tooltip below
                  const dayEvents = getEventsForDay(day);
                  const hasEvents = dayEvents.length > 0;
                  
                  // Get unique event categories for this day (max 3 dots)
                  const eventCategories = [...new Set(dayEvents.map(e => getEventCategory(e.subject)))].slice(0, 3);
                  
                  return (
                    <div 
                      key={day}
                      className={`h-14 flex flex-col items-center justify-center text-sm cursor-pointer transition-colors bg-black relative group
                        ${!isLastCol ? 'border-r border-white' : ''}
                        ${!isLastRow ? 'border-b border-white' : ''}
                        ${isToday(day) ? 'bg-white text-black font-bold' : 'text-white hover:bg-gray-800'}
                      `}
                    >
                      <span>{day}</span>
                      {/* Event dots */}
                      {hasEvents && (
                        <div className="flex gap-0.5 mt-0.5">
                          {eventCategories.map((category, i) => (
                            <div 
                              key={i} 
                              className={`w-1.5 h-1.5 rounded-full ${eventCategoryColors[category] || eventCategoryColors.default}`}
                            />
                          ))}
                        </div>
                      )}
                      
                      {/* Hover tooltip showing events - positioned based on row */}
                      {hasEvents && (
                        <div 
                          className={`absolute ${isTopHalf ? 'top-full mt-2' : 'bottom-full mb-2'} left-1/2 -translate-x-1/2 hidden group-hover:block pointer-events-none`}
                          style={{ zIndex: 9999 }}
                        >
                          <div className="bg-gray-900 border border-gray-600 rounded-lg p-3 shadow-2xl min-w-[220px] max-w-[300px]">
                            <div className="text-xs font-bold text-white mb-2 border-b border-gray-700 pb-1">
                              {new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </div>
                            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                              {dayEvents.map((event, i) => {
                                const category = getEventCategory(event.subject);
                                const time = event.start?.dateTime ? new Date(event.start.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
                                return (
                                  <div key={i} className="flex items-start gap-2">
                                    <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${eventCategoryColors[category]}`} />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white font-medium">{event.subject}</p>
                                      {time && <p className="text-[10px] text-gray-400">{time}</p>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {/* Arrow - positioned based on tooltip direction */}
                            <div className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-transparent ${
                              isTopHalf 
                                ? 'bottom-full border-b-[6px] border-b-gray-600' 
                                : 'top-full border-t-[6px] border-t-gray-600'
                            }`} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Fill remaining cells to complete the grid */}
                {Array.from({ length: (7 - ((getFirstDayOfMonth(calendarDate) + getDaysInMonth(calendarDate)) % 7)) % 7 }).map((_, idx) => (
                  <div key={`fill-${idx}`} className={`h-14 border-b border-white bg-black ${idx < 6 ? 'border-r border-white' : ''}`}></div>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Universal Modal */}
        {activeModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6" onClick={closeModal}>
            <div className="bg-black border-2 border-gray-400 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-black border-b border-gray-400 p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold">
                  {activeModal === 'portfolio' && 'Portfolio Details'}
                  {activeModal === 'financial' && 'Financial Breakdown'}
                  {activeModal === 'lease' && 'Lease Schedule Details'}
                  {activeModal === 'pending' && 'Pending Transactions'}
                  {activeModal === 'revenue' && 'Revenue & Expense Analysis'}
                  {activeModal === 'occupancy' && 'Occupancy Distribution'}
                  {activeModal === 'pipeline' && 'Deal Pipeline Details'}
                  {activeModal === 'market' && 'Market Indicators'}
                  {activeModal === 'status' && 'Property Status Breakdown'}
                  {activeModal === 'time-schedule' && 'Schedule & Calendar'}
                  {activeModal === 'weather' && 'Weather Forecast'}
                  {activeModal === 'revenue-detail' && 'Revenue Overview'}
                  {activeModal === 'properties-detail' && 'Properties Overview'}
                  {activeModal === 'brightness' && 'Display Settings'}
                  {activeModal === 'volume' && 'Audio Settings'}
                  {activeModal === 'music-player' && 'Now Playing'}
                  {activeModal === 'calculator' && 'Calculator'}
                  {activeModal === 'voice-recorder' && 'Voice Recorder'}
                  {activeModal === 'alarm' && 'Alarm & Reminders'}
                  {activeModal === 'notes' && 'Quick Notes'}
                  {activeModal === 'task' && 'Task Details'}
                  {activeModal === 'unread-messages' && 'Unread Messages'}
                  {activeModal === 'todays-meetings' && 'Today\'s Meetings'}
                </h2>
                <button onClick={closeModal} className="hover:bg-gray-900 p-2 rounded-lg transition-colors">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6">
                {/* Portfolio Modal Content */}
                {activeModal === 'portfolio' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-cyan-500/10 border border-cyan-500 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-2">Active Properties</div>
                        <div className="text-3xl font-bold text-white mb-2">38</div>
                        <div className="text-sm text-white">$187.2M Total Value</div>
                        <div className="mt-4 space-y-2 text-sm">
                          <div className="flex justify-between"><span>Commercial:</span><span className="font-bold">18</span></div>
                          <div className="flex justify-between"><span>Residential:</span><span className="font-bold">12</span></div>
                          <div className="flex justify-between"><span>Mixed-Use:</span><span className="font-bold">8</span></div>
                        </div>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-2">Upcoming Projects</div>
                        <div className="text-3xl font-bold text-purple-400 mb-2">5</div>
                        <div className="text-sm text-purple-400">$38.6M Estimated Value</div>
                        <div className="mt-4 space-y-2 text-sm">
                          <div className="flex justify-between"><span>Q1 2026:</span><span className="font-bold">2</span></div>
                          <div className="flex justify-between"><span>Q2 2026:</span><span className="font-bold">3</span></div>
                        </div>
                      </div>
                      <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-2">Completed</div>
                        <div className="text-3xl font-bold text-yellow-400 mb-2">4</div>
                        <div className="text-sm text-yellow-400">$19.2M Final Value</div>
                        <div className="mt-4 space-y-2 text-sm">
                          <div className="flex justify-between"><span>2025:</span><span className="font-bold">4</span></div>
                          <div className="flex justify-between"><span>Avg ROI:</span><span className="font-bold">18.5%</span></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-800 pt-4">
                      <h3 className="font-bold mb-3">Recent Activity</h3>
                      <div className="space-y-2">
                        <div className="bg-gray-900 rounded-lg p-3 flex justify-between items-center">
                          <div>
                            <div className="font-semibold">Harbor View Apartments</div>
                            <div className="text-sm text-gray-400">Lease renewal completed - $2.4M annual</div>
                          </div>
                          <div className="text-xs text-green-400">2 days ago</div>
                        </div>
                        <div className="bg-gray-900 rounded-lg p-3 flex justify-between items-center">
                          <div>
                            <div className="font-semibold">Downtown Plaza</div>
                            <div className="text-sm text-gray-400">New tenant signed - 5-year lease</div>
                          </div>
                          <div className="text-xs text-green-400">5 days ago</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Financial Modal Content */}
                {activeModal === 'financial' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-bold mb-4">Revenue Breakdown</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                            <span>Commercial Leases</span>
                            <span className="font-bold text-green-400">$24.8M</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                            <span>Residential Leases</span>
                            <span className="font-bold text-green-400">$12.3M</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                            <span>Parking & Amenities</span>
                            <span className="font-bold text-green-400">$3.2M</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                            <span>Other Income</span>
                            <span className="font-bold text-green-400">$2.5M</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-bold mb-4">Expense Breakdown</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                            <span>Property Maintenance</span>
                            <span className="font-bold text-red-400">$8.4M</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                            <span>Property Management</span>
                            <span className="font-bold text-red-400">$3.8M</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                            <span>Utilities</span>
                            <span className="font-bold text-red-400">$2.9M</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                            <span>Insurance & Taxes</span>
                            <span className="font-bold text-red-400">$4.2M</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-800 pt-4">
                      <h3 className="font-bold mb-3">Key Metrics</h3>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-gray-900 rounded-lg">
                          <div className="text-2xl font-bold text-green-400">67%</div>
                          <div className="text-sm text-gray-400 mt-1">Profit Margin</div>
                        </div>
                        <div className="text-center p-4 bg-gray-900 rounded-lg">
                          <div className="text-2xl font-bold text-white">$8.3M</div>
                          <div className="text-sm text-gray-400 mt-1">Remaining Budget</div>
                        </div>
                        <div className="text-center p-4 bg-gray-900 rounded-lg">
                          <div className="text-2xl font-bold text-purple-400">14.2%</div>
                          <div className="text-sm text-gray-400 mt-1">YoY Growth</div>
                        </div>
                        <div className="text-center p-4 bg-gray-900 rounded-lg">
                          <div className="text-2xl font-bold text-amber-400">$156K</div>
                          <div className="text-sm text-gray-400 mt-1">Avg Monthly Revenue</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Deal Pipeline Modal Content */}
                {activeModal === 'pipeline' && (
                  <div className="space-y-4">
                    <div className="bg-lime-500/10 border-l-4 border-lime-500 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-lg">Negotiation Stage</h3>
                          <div className="text-sm text-gray-400">12 Active Deals</div>
                        </div>
                        <div className="text-2xl font-bold text-lime-400">$18.5M</div>
                      </div>
                      <div className="space-y-2">
                        <div className="bg-black/50 rounded p-2 text-sm flex justify-between">
                          <span>Manhattan Office Complex</span><span className="text-lime-400">$7.2M</span>
                        </div>
                        <div className="bg-black/50 rounded p-2 text-sm flex justify-between">
                          <span>Brooklyn Mixed-Use Development</span><span className="text-lime-400">$4.8M</span>
                        </div>
                        <div className="bg-black/50 rounded p-2 text-sm flex justify-between">
                          <span>Queens Residential Tower</span><span className="text-lime-400">$3.5M</span>
                        </div>
                        <div className="text-xs text-gray-500">+ 9 more deals</div>
                      </div>
                    </div>

                    <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-lg">Due Diligence</h3>
                          <div className="text-sm text-gray-400">8 Properties Under Review</div>
                        </div>
                        <div className="text-2xl font-bold text-amber-400">$12.2M</div>
                      </div>
                      <div className="space-y-2">
                        <div className="bg-black/50 rounded p-2 text-sm flex justify-between">
                          <span>Bronx Retail Center</span><span className="text-amber-400">$5.1M</span>
                        </div>
                        <div className="bg-black/50 rounded p-2 text-sm flex justify-between">
                          <span>Staten Island Industrial Park</span><span className="text-amber-400">$4.2M</span>
                        </div>
                        <div className="text-xs text-gray-500">+ 6 more properties</div>
                      </div>
                    </div>

                    <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-lg">Closing Stage</h3>
                          <div className="text-sm text-gray-400">5 Deals Finalizing</div>
                        </div>
                        <div className="text-2xl font-bold text-white">$8.7M</div>
                      </div>
                      <div className="space-y-2">
                        <div className="bg-black/50 rounded p-2 text-sm flex justify-between">
                          <span>Hudson Yards Apartments</span><span className="text-white">$3.8M</span>
                        </div>
                        <div className="bg-black/50 rounded p-2 text-sm flex justify-between">
                          <span>Lower East Side Condos</span><span className="text-white">$2.9M</span>
                        </div>
                        <div className="text-xs text-gray-500">+ 3 more deals</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Time & Schedule Modal */}
                {activeModal === 'time-schedule' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-6">
                      <div className="text-center mb-6">
                        <div className="text-6xl font-bold mb-2">{currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</div>
                        <div className="text-xl text-gray-400">{currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
                        <div className="text-sm text-gray-500 mt-2">Local Time</div>
                      </div>
                    </div>
                    
                    {/* World Clocks */}
                    <div className="grid grid-cols-3 gap-4">
                      {/* Los Angeles */}
                      <div className="bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/50 rounded-lg p-4 text-center">
                        <div className="text-sm text-orange-400 font-medium mb-2">🌴 Los Angeles</div>
                        <div className="text-2xl font-bold font-mono">
                          {currentTime.toLocaleTimeString('en-US', { 
                            timeZone: 'America/Los_Angeles',
                            hour: '2-digit', 
                            minute: '2-digit', 
                            second: '2-digit',
                            hour12: true 
                          })}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {currentTime.toLocaleDateString('en-US', { 
                            timeZone: 'America/Los_Angeles',
                            weekday: 'short',
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">PST/PDT</div>
                      </div>
                      
                      {/* New York City */}
                      <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
                        <div className="text-sm text-white font-medium mb-2">🗽 New York City</div>
                        <div className="text-2xl font-bold font-mono">
                          {currentTime.toLocaleTimeString('en-US', { 
                            timeZone: 'America/New_York',
                            hour: '2-digit', 
                            minute: '2-digit', 
                            second: '2-digit',
                            hour12: true 
                          })}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {currentTime.toLocaleDateString('en-US', { 
                            timeZone: 'America/New_York',
                            weekday: 'short',
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">EST/EDT</div>
                      </div>
                      
                      {/* Philippines */}
                      <div className="bg-gradient-to-br from-red-500/20 to-yellow-500/20 border border-red-500/50 rounded-lg p-4 text-center">
                        <div className="text-sm text-red-400 font-medium mb-2">🇵🇭 Philippines</div>
                        <div className="text-2xl font-bold font-mono">
                          {currentTime.toLocaleTimeString('en-US', { 
                            timeZone: 'Asia/Manila',
                            hour: '2-digit', 
                            minute: '2-digit', 
                            second: '2-digit',
                            hour12: true 
                          })}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {currentTime.toLocaleDateString('en-US', { 
                            timeZone: 'Asia/Manila',
                            weekday: 'short',
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">PHT (UTC+8)</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Weather Modal */}
                {activeModal === 'weather' && (
                  <div className="space-y-4">
                    <div 
                      className="rounded-lg p-6 text-center"
                      style={{
                        background: weatherData ? getWeatherBackground(weatherData.current?.weather_code, weatherData.current?.temperature_2m) : 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)'
                      }}
                    >
                      {weatherLoading ? (
                        <Loader2 size={64} className="text-white mx-auto mb-4 animate-spin" />
                      ) : (
                        getWeatherIcon(weatherData?.current?.weather_code || 0, 'w-16 h-16 mx-auto mb-4')
                      )}
                      <div className="text-6xl font-bold mb-2">
                        {weatherLoading ? '--' : Math.round(weatherData?.current?.temperature_2m || 0)}°F
                      </div>
                      <div className="text-xl text-white/90 mb-1">
                        {weatherLoading ? 'Loading...' : getWeatherDescription(weatherData?.current?.weather_code || 0)}
                      </div>
                      {weatherLocation && (
                        <div className="text-sm text-white/70 mb-4">{weatherLocation}</div>
                      )}
                      <div className="grid grid-cols-4 gap-4 text-sm mt-4">
                        <div className="bg-white/20 rounded-lg p-2">
                          <div className="text-white/70">Feels Like</div>
                          <div className="font-bold text-lg">
                            {weatherLoading ? '--' : Math.round(weatherData?.current?.apparent_temperature || 0)}°
                          </div>
                        </div>
                        <div className="bg-white/20 rounded-lg p-2">
                          <div className="text-white/70">Humidity</div>
                          <div className="font-bold text-lg">
                            {weatherLoading ? '--' : Math.round(weatherData?.current?.relative_humidity_2m || 0)}%
                          </div>
                        </div>
                        <div className="bg-white/20 rounded-lg p-2">
                          <div className="text-white/70">Wind</div>
                          <div className="font-bold text-lg">
                            {weatherLoading ? '--' : Math.round(weatherData?.current?.wind_speed_10m || 0)} mph
                          </div>
                        </div>
                        <div className="bg-white/20 rounded-lg p-2">
                          <div className="text-white/70">UV Index</div>
                          <div className="font-bold text-lg">
                            {weatherLoading ? '--' : Math.round(weatherData?.current?.uv_index || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4">
                      <div className="text-sm font-semibold mb-3">5-Day Forecast</div>
                      <div className="grid grid-cols-5 gap-2 text-center text-xs">
                        {weatherData?.daily?.time?.slice(0, 5).map((day, idx) => {
                          // Parse date correctly - add T00:00:00 to avoid UTC timezone issues
                          const dateObj = new Date(day + 'T00:00:00');
                          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                          const weatherCode = weatherData.daily.weather_code?.[idx] || 0;
                          const maxTemp = Math.round(weatherData.daily.temperature_2m_max?.[idx] || 0);
                          const minTemp = Math.round(weatherData.daily.temperature_2m_min?.[idx] || 0);
                          return (
                            <div key={idx} className="p-3 bg-gray-800 rounded-lg">
                              <div className="mb-2 font-medium">{dayName}</div>
                              {getWeatherIcon(weatherCode, 'w-8 h-8 mx-auto')}
                              <div className="mt-2 font-bold">{maxTemp}°</div>
                              <div className="text-gray-500">{minTemp}°</div>
                            </div>
                          );
                        }) || (
                          ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, idx) => (
                            <div key={idx} className="p-3 bg-gray-800 rounded-lg">
                              <div className="mb-2 font-medium">{day}</div>
                              <div className="w-8 h-8 mx-auto bg-gray-700 rounded animate-pulse"></div>
                              <div className="mt-2">--°</div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Revenue Detail Modal */}
                {activeModal === 'revenue-detail' && (
                  <div className="space-y-4">
                    <div className="bg-green-500/10 border border-green-500 rounded-lg p-6">
                      <div className="text-sm text-gray-400 mb-2">Total Revenue (YTD)</div>
                      <div className="text-5xl font-bold text-green-400 mb-2">$42.8M</div>
                      <div className="flex items-center gap-2 text-sm">
                        <ArrowUp size={16} className="text-white" />
                        <span className="text-green-400 font-semibold">+12.4%</span>
                        <span className="text-gray-400">vs. last year</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-900 rounded-lg p-4">
                        <div className="text-xs text-gray-400 mb-1">Rental Income</div>
                        <div className="text-2xl font-bold">$35.2M</div>
                        <div className="text-xs text-green-400 mt-1">+8.5%</div>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4">
                        <div className="text-xs text-gray-400 mb-1">Property Sales</div>
                        <div className="text-2xl font-bold">$5.8M</div>
                        <div className="text-xs text-green-400 mt-1">+24.3%</div>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4">
                        <div className="text-xs text-gray-400 mb-1">Other Income</div>
                        <div className="text-2xl font-bold">$1.8M</div>
                        <div className="text-xs text-green-400 mt-1">+15.7%</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Properties Detail Modal */}
                {activeModal === 'properties-detail' && (
                  <div className="space-y-4">
                    <div className="bg-purple-500/10 border border-purple-500 rounded-lg p-6">
                      <div className="text-sm text-gray-400 mb-2">Total Properties</div>
                      <div className="text-5xl font-bold text-purple-400 mb-2">47 Active</div>
                      <div className="text-sm text-gray-300">Portfolio Value: $245M</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-900 rounded-lg p-4">
                        <div className="text-xs text-gray-400 mb-1">Commercial</div>
                        <div className="text-2xl font-bold">18</div>
                        <div className="text-xs text-white mt-1">$128M</div>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4">
                        <div className="text-xs text-gray-400 mb-1">Residential</div>
                        <div className="text-2xl font-bold">22</div>
                        <div className="text-xs text-purple-400 mt-1">$92M</div>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4">
                        <div className="text-xs text-gray-400 mb-1">Mixed-Use</div>
                        <div className="text-2xl font-bold">7</div>
                        <div className="text-xs text-yellow-400 mt-1">$25M</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Brightness Modal */}
                {activeModal === 'brightness' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-6">
                      <div className="text-center mb-6">
                        <Sun size={48} className="text-white mx-auto mb-4" />
                        <div className="text-2xl font-bold mb-2">Display Brightness</div>
                        <div className="text-gray-400">Currently set to Auto (70%)</div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-2 text-sm">
                            <span>Brightness Level</span>
                            <span className="font-bold">70%</span>
                          </div>
                          <div className="bg-gray-700 rounded-full h-4">
                            <div className="bg-white rounded-full h-4" style={{ width: '70%' }}></div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <button className="bg-gray-800 hover:bg-gray-700 p-2 rounded text-sm">25%</button>
                          <button className="bg-gray-800 hover:bg-gray-700 p-2 rounded text-sm">50%</button>
                          <button className="bg-gray-800 hover:bg-gray-700 p-2 rounded text-sm">100%</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Volume Modal */}
                {activeModal === 'volume' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-6">
                      <div className="text-center mb-6">
                        <svg className="w-16 h-16 text-white mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                        </svg>
                        <div className="text-2xl font-bold mb-2">Audio Settings</div>
                        <div className="text-gray-400">System Volume: 45%</div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-2 text-sm">
                            <span>Master Volume</span>
                            <span className="font-bold">45%</span>
                          </div>
                          <div className="bg-gray-700 rounded-full h-4">
                            <div className="bg-blue-400 rounded-full h-4" style={{ width: '45%' }}></div>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          <button className="bg-gray-800 hover:bg-gray-700 p-2 rounded text-sm">Mute</button>
                          <button className="bg-gray-800 hover:bg-gray-700 p-2 rounded text-sm">25%</button>
                          <button className="bg-gray-800 hover:bg-gray-700 p-2 rounded text-sm">50%</button>
                          <button className="bg-gray-800 hover:bg-gray-700 p-2 rounded text-sm">100%</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Music Player Modal */}
                {activeModal === 'music-player' && (
                  <div className="space-y-4">
                    {/* Connection Screen */}
                    {!spotifyConnected ? (
                      <div className="bg-gradient-to-br from-green-900/40 to-black border border-green-500/30 rounded-lg p-8 text-center">
                        <svg className="w-20 h-20 mx-auto mb-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                        </svg>
                        <h3 className="text-2xl font-bold mb-2">Connect to Spotify</h3>
                        <p className="text-gray-400 mb-6">Link your Spotify account to control playback and see song info</p>
                        <button
                          onClick={connectToSpotify}
                          disabled={spotifyLoading}
                          className="px-8 py-3 bg-green-500 hover:bg-green-600 disabled:bg-green-800 rounded-full font-semibold transition-colors flex items-center gap-2 mx-auto text-lg"
                        >
                          {spotifyLoading ? <Loader2 size={20} className="animate-spin" /> : null}
                          Connect Spotify
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* Tab Navigation */}
                        <div className="flex gap-1 bg-gray-900 p-1 rounded-lg">
                          <button
                            onClick={() => setSpotifyModalTab('player')}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${spotifyModalTab === 'player' ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'}`}
                          >
                            Now Playing
                          </button>
                          <button
                            onClick={() => {
                              setSpotifyModalTab('artist');
                              if (currentTrack?.artistIds?.[0] && !spotifyArtistInfo) {
                                fetchArtistInfo(currentTrack.artistIds[0]);
                              }
                            }}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${spotifyModalTab === 'artist' ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'}`}
                          >
                            Artist
                          </button>
                          <button
                            onClick={() => {
                              setSpotifyModalTab('track');
                              if (currentTrack?.id && !spotifyTrackDetails) {
                                fetchTrackDetails(currentTrack.id);
                              }
                            }}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${spotifyModalTab === 'track' ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'}`}
                          >
                            Track Info
                          </button>
                        </div>

                        {/* NOW PLAYING TAB */}
                        {spotifyModalTab === 'player' && (
                          <div className="bg-gradient-to-br from-green-900/40 to-black border border-green-500/30 rounded-lg p-6">
                            {/* Large Album Art */}
                            <div className="flex flex-col items-center mb-6">
                              <div className="relative mb-4">
                                <img 
                                  src={currentTrack?.album_art || "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=300&h=300&fit=crop"} 
                                  alt="Album Cover"
                                  className={`w-48 h-48 rounded-xl object-cover shadow-2xl ${isPlaying ? '' : 'opacity-80'}`}
                                />
                                {isPlaying && (
                                  <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-2 shadow-lg">
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                                    </svg>
                                  </div>
                                )}
                              </div>
                              
                              {/* Track Info */}
                              <div className="text-center w-full">
                                <h3 className="text-2xl font-bold mb-1 truncate">{currentTrack?.name || 'No track playing'}</h3>
                                <p className="text-lg text-gray-400 mb-1">{currentTrack?.artist || 'Play something on Spotify'}</p>
                                <p className="text-sm text-gray-500">{currentTrack?.album || ''}</p>
                                {currentTrack?.explicit && (
                                  <span className="inline-block mt-2 px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded font-medium">EXPLICIT</span>
                                )}
                              </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="space-y-2 mb-6">
                              <input
                                type="range"
                                min="0"
                                max={currentTrack?.duration || 100}
                                value={spotifyProgress}
                                onChange={(e) => setSpotifyProgress(parseInt(e.target.value))}
                                onMouseUp={(e) => spotifySeek(parseInt(e.target.value))}
                                onTouchEnd={(e) => spotifySeek(parseInt(e.target.value))}
                                className="w-full h-2 bg-gray-700 rounded-full appearance-none cursor-pointer"
                                style={{
                                  background: `linear-gradient(to right, #1DB954 ${currentTrack?.duration ? (spotifyProgress / currentTrack.duration) * 100 : 0}%, rgb(55, 65, 81) ${currentTrack?.duration ? (spotifyProgress / currentTrack.duration) * 100 : 0}%)`
                                }}
                              />
                              <div className="flex justify-between text-xs text-gray-400">
                                <span>{Math.floor(spotifyProgress / 60000)}:{String(Math.floor((spotifyProgress % 60000) / 1000)).padStart(2, '0')}</span>
                                <span>{currentTrack?.duration ? `${Math.floor(currentTrack.duration / 60000)}:${String(Math.floor((currentTrack.duration % 60000) / 1000)).padStart(2, '0')}` : '0:00'}</span>
                              </div>
                            </div>

                            {/* Main Controls */}
                            <div className="flex items-center justify-center gap-6 mb-6">
                              <button onClick={spotifyToggleShuffle} className={`p-2 rounded-full transition-colors ${spotifyShuffle ? 'text-green-500' : 'text-gray-400 hover:text-white'}`}>
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/></svg>
                              </button>
                              <button onClick={spotifyPrev} className="p-2 text-white hover:text-green-400 transition-colors">
                                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>
                              </button>
                              <button onClick={spotifyPlayPause} className="w-16 h-16 bg-green-500 hover:bg-green-400 hover:scale-105 rounded-full flex items-center justify-center transition-all shadow-lg">
                                {isPlaying ? (
                                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>
                                ) : (
                                  <svg className="w-8 h-8 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                )}
                              </button>
                              <button onClick={spotifyNext} className="p-2 text-white hover:text-green-400 transition-colors">
                                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/></svg>
                              </button>
                              <button onClick={spotifyToggleRepeat} className={`p-2 rounded-full transition-colors relative ${spotifyRepeat !== 'off' ? 'text-green-500' : 'text-gray-400 hover:text-white'}`}>
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/></svg>
                                {spotifyRepeat === 'track' && <span className="absolute -top-1 -right-1 text-[10px] font-bold text-green-500">1</span>}
                              </button>
                            </div>

                            {/* Volume */}
                            <div className="flex items-center gap-3">
                              <button onClick={() => spotifySetVolume(spotifyVolume === 0 ? 50 : 0)} className="text-gray-400 hover:text-white transition-colors">
                                {spotifyVolume === 0 ? (
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
                                ) : (
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
                                )}
                              </button>
                              <input type="range" min="0" max="100" value={spotifyVolume} onChange={(e) => spotifySetVolume(parseInt(e.target.value))} className="flex-1 h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer" style={{ background: `linear-gradient(to right, #1DB954 ${spotifyVolume}%, rgb(55, 65, 81) ${spotifyVolume}%)` }} />
                              <span className="text-xs text-gray-400 w-8">{spotifyVolume}%</span>
                            </div>
                          </div>
                        )}

                        {/* ARTIST TAB */}
                        {spotifyModalTab === 'artist' && (
                          <div className="bg-gray-900 rounded-lg overflow-hidden">
                            {spotifyLoadingDetails ? (
                              <div className="flex items-center justify-center py-16">
                                <Loader2 className="w-8 h-8 animate-spin text-green-500" />
                              </div>
                            ) : spotifyArtistInfo ? (
                              <div>
                                {/* Artist Header */}
                                <div className="relative h-48 bg-gradient-to-b from-green-900/60 to-gray-900">
                                  {spotifyArtistInfo.images?.[0]?.url && (
                                    <img src={spotifyArtistInfo.images[0].url} alt={spotifyArtistInfo.name} className="absolute inset-0 w-full h-full object-cover opacity-40" />
                                  )}
                                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
                                  <div className="absolute bottom-4 left-4 right-4">
                                    <div className="flex items-end gap-4">
                                      <img src={spotifyArtistInfo.images?.[0]?.url || 'https://via.placeholder.com/100'} alt={spotifyArtistInfo.name} className="w-24 h-24 rounded-full border-4 border-gray-900 shadow-xl object-cover" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-green-400 font-medium uppercase tracking-wider">Artist</p>
                                        <h3 className="text-2xl font-bold truncate">{spotifyArtistInfo.name}</h3>
                                        <p className="text-sm text-gray-400">{spotifyArtistInfo.followers?.toLocaleString()} followers</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Artist Details */}
                                <div className="p-4 space-y-4">
                                  {/* Popularity & Genres */}
                                  <div className="flex flex-wrap gap-2">
                                    <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1.5 rounded-full">
                                      <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                                      <span className="text-sm text-green-400 font-medium">Popularity: {spotifyArtistInfo.popularity}%</span>
                                    </div>
                                    {spotifyArtistInfo.genres?.slice(0, 3).map((genre, idx) => (
                                      <span key={idx} className="px-3 py-1.5 bg-gray-800 rounded-full text-sm text-gray-300 capitalize">{genre}</span>
                                    ))}
                                  </div>

                                  {/* Top Tracks */}
                                  {spotifyArtistInfo.top_tracks?.length > 0 && (
                                    <div>
                                      <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Top Tracks</h4>
                                      <div className="space-y-1">
                                        {spotifyArtistInfo.top_tracks.map((track, idx) => (
                                          <div key={track.id} className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded-lg transition-colors cursor-pointer group">
                                            <span className="w-5 text-center text-gray-500 text-sm">{idx + 1}</span>
                                            <img src={track.album_art || 'https://via.placeholder.com/40'} alt="" className="w-10 h-10 rounded object-cover" />
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium truncate">{track.name}</p>
                                              <p className="text-xs text-gray-500 truncate">{track.album}</p>
                                            </div>
                                            <span className="text-xs text-gray-500">{Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Related Artists */}
                                  {spotifyArtistInfo.related_artists?.length > 0 && (
                                    <div>
                                      <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Fans Also Like</h4>
                                      <div className="grid grid-cols-3 gap-2">
                                        {spotifyArtistInfo.related_artists.map((artist) => (
                                          <div key={artist.id} className="flex flex-col items-center p-2 hover:bg-gray-800 rounded-lg transition-colors cursor-pointer">
                                            <img src={artist.image || 'https://via.placeholder.com/60'} alt={artist.name} className="w-14 h-14 rounded-full object-cover mb-1" />
                                            <p className="text-xs text-center truncate w-full">{artist.name}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-12 text-gray-500">
                                <p>Play a track to see artist info</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* TRACK INFO TAB */}
                        {spotifyModalTab === 'track' && (
                          <div className="bg-gray-900 rounded-lg p-4">
                            {spotifyLoadingDetails ? (
                              <div className="flex items-center justify-center py-16">
                                <Loader2 className="w-8 h-8 animate-spin text-green-500" />
                              </div>
                            ) : spotifyTrackDetails ? (
                              <div className="space-y-4">
                                {/* Track Header */}
                                <div className="flex gap-4">
                                  <img src={spotifyTrackDetails.album?.images?.[0]?.url || 'https://via.placeholder.com/120'} alt="" className="w-28 h-28 rounded-lg shadow-lg object-cover" />
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-xl font-bold truncate">{spotifyTrackDetails.name}</h3>
                                    <p className="text-gray-400">{spotifyTrackDetails.artists?.map(a => a.name).join(', ')}</p>
                                    <p className="text-sm text-gray-500">{spotifyTrackDetails.album?.name}</p>
                                    <div className="flex gap-2 mt-2">
                                      {spotifyTrackDetails.explicit && (
                                        <span className="px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded font-medium">EXPLICIT</span>
                                      )}
                                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded font-medium">{spotifyTrackDetails.popularity}% Popular</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Album Info */}
                                <div className="bg-gray-800/50 rounded-lg p-3">
                                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Album Details</h4>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><span className="text-gray-500">Release:</span> <span className="text-gray-300">{spotifyTrackDetails.album?.release_date || 'N/A'}</span></div>
                                    <div><span className="text-gray-500">Track:</span> <span className="text-gray-300">{spotifyTrackDetails.track_number} of {spotifyTrackDetails.album?.total_tracks}</span></div>
                                    <div><span className="text-gray-500">Type:</span> <span className="text-gray-300 capitalize">{spotifyTrackDetails.album?.album_type || 'N/A'}</span></div>
                                    <div><span className="text-gray-500">Label:</span> <span className="text-gray-300">{spotifyTrackDetails.album?.label || 'N/A'}</span></div>
                                  </div>
                                </div>

                                {/* Audio Features */}
                                {spotifyTrackDetails.audio_features && (
                                  <div className="bg-gray-800/50 rounded-lg p-3">
                                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Audio Analysis</h4>
                                    <div className="space-y-2">
                                      {[
                                        { label: 'Energy', value: spotifyTrackDetails.audio_features.energy, color: 'bg-red-500' },
                                        { label: 'Danceability', value: spotifyTrackDetails.audio_features.danceability, color: 'bg-purple-500' },
                                        { label: 'Happiness', value: spotifyTrackDetails.audio_features.valence, color: 'bg-yellow-500' },
                                        { label: 'Acousticness', value: spotifyTrackDetails.audio_features.acousticness, color: 'bg-green-500' },
                                        { label: 'Instrumentalness', value: spotifyTrackDetails.audio_features.instrumentalness, color: 'bg-blue-500' },
                                      ].map((feature) => (
                                        <div key={feature.label} className="flex items-center gap-2">
                                          <span className="text-xs text-gray-400 w-28">{feature.label}</span>
                                          <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                                            <div className={`h-full ${feature.color} rounded-full transition-all`} style={{ width: `${(feature.value || 0) * 100}%` }}></div>
                                          </div>
                                          <span className="text-xs text-gray-500 w-10 text-right">{Math.round((feature.value || 0) * 100)}%</span>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-gray-700 flex flex-wrap gap-3 text-sm">
                                      {spotifyTrackDetails.audio_features.tempo && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-gray-500">Tempo:</span>
                                          <span className="text-gray-300">{Math.round(spotifyTrackDetails.audio_features.tempo)} BPM</span>
                                        </div>
                                      )}
                                      {spotifyTrackDetails.audio_features.key !== null && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-gray-500">Key:</span>
                                          <span className="text-gray-300">{['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'][spotifyTrackDetails.audio_features.key]} {spotifyTrackDetails.audio_features.mode === 1 ? 'Major' : 'Minor'}</span>
                                        </div>
                                      )}
                                      {spotifyTrackDetails.audio_features.time_signature && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-gray-500">Time:</span>
                                          <span className="text-gray-300">{spotifyTrackDetails.audio_features.time_signature}/4</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Copyright */}
                                {spotifyTrackDetails.album?.copyrights?.length > 0 && (
                                  <div className="text-xs text-gray-600 pt-2">
                                    {spotifyTrackDetails.album.copyrights[0]?.text}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-12 text-gray-500">
                                <p>Play a track to see detailed info</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Devices Section - Always visible */}
                        <div className="bg-gray-900 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-semibold flex items-center gap-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M4 6h18V4H4c-1.1 0-2 .9-2 2v11H0v3h14v-3H4V6zm19 2h-6c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h6c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1zm-1 9h-4v-7h4v7z"/></svg>
                              Listening On
                            </div>
                            <button onClick={() => fetchSpotifyDevices(spotifyToken)} className="text-xs text-green-500 hover:text-green-400">Refresh</button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {spotifyDevices.length === 0 ? (
                              <p className="text-sm text-gray-500">No devices found. Open Spotify on a device.</p>
                            ) : (
                              spotifyDevices.map((device) => (
                                <button
                                  key={device.id}
                                  onClick={() => spotifyTransferPlayback(device.id)}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${device.is_active ? 'bg-green-500/20 border border-green-500/50 text-green-400' : 'bg-gray-800 hover:bg-gray-700 text-gray-300'}`}
                                >
                                  {device.type === 'Computer' ? (
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/></svg>
                                  ) : device.type === 'Smartphone' ? (
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/></svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
                                  )}
                                  <span className="text-sm">{device.name}</span>
                                  {device.is_active && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>}
                                </button>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Disconnect */}
                        <div className="text-center">
                          <button onClick={disconnectSpotify} className="text-xs text-gray-500 hover:text-red-400 transition-colors">
                            Disconnect Spotify
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Calculator Modal */}
                {activeModal === 'calculator' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-6">
                      <div className="bg-black rounded-lg p-4 mb-4 text-right">
                        <div className="text-4xl font-bold text-white mb-2 truncate">{calcDisplay}</div>
                        <div className="text-sm text-gray-400">{calcOperator ? `${calcPrevValue} ${calcOperator}` : 'Ready'}</div>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <button onClick={calcClear} className="bg-red-600 hover:bg-red-700 text-white p-4 rounded-lg font-semibold text-lg col-span-2">C</button>
                        <button onClick={() => setCalcDisplay(calcDisplay.slice(0, -1) || '0')} className="bg-gray-700 hover:bg-gray-600 text-white p-4 rounded-lg font-semibold text-lg">⌫</button>
                        <button onClick={() => calcPerformOperation('÷')} className="bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-lg font-semibold text-lg">÷</button>
                        
                        {['7', '8', '9'].map((btn) => (
                          <button key={btn} onClick={() => calcInputDigit(btn)} className="bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-lg font-semibold text-lg">{btn}</button>
                        ))}
                        <button onClick={() => calcPerformOperation('×')} className="bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-lg font-semibold text-lg">×</button>
                        
                        {['4', '5', '6'].map((btn) => (
                          <button key={btn} onClick={() => calcInputDigit(btn)} className="bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-lg font-semibold text-lg">{btn}</button>
                        ))}
                        <button onClick={() => calcPerformOperation('-')} className="bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-lg font-semibold text-lg">−</button>
                        
                        {['1', '2', '3'].map((btn) => (
                          <button key={btn} onClick={() => calcInputDigit(btn)} className="bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-lg font-semibold text-lg">{btn}</button>
                        ))}
                        <button onClick={() => calcPerformOperation('+')} className="bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-lg font-semibold text-lg">+</button>
                        
                        <button onClick={() => calcInputDigit('0')} className="bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-lg font-semibold text-lg col-span-2">0</button>
                        <button onClick={calcInputDecimal} className="bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-lg font-semibold text-lg">.</button>
                        <button onClick={calcEquals} className="bg-blue-500 hover:bg-blue-600 text-white p-4 rounded-lg font-semibold text-lg">=</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Voice Recorder Modal */}
                {activeModal === 'voice-recorder' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-6 text-center">
                      <div 
                        onClick={isRecordingVoice ? stopVoiceRecording : startVoiceRecording}
                        className={`w-24 h-24 ${isRecordingVoice ? 'bg-red-500 animate-pulse' : 'bg-gray-700 hover:bg-gray-600'} rounded-full flex items-center justify-center mx-auto mb-4 cursor-pointer transition-colors`}
                      >
                        <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="2"/>
                          <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </div>
                      <div className="text-2xl font-bold mb-2">{isRecordingVoice ? 'Recording...' : 'Tap to Record'}</div>
                      <div className="text-gray-400 mb-4 text-3xl font-mono">{formatRecordingTime(recordingTime)}</div>
                      {isRecordingVoice && (
                        <button onClick={stopVoiceRecording} className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-semibold">
                          Stop Recording
                        </button>
                      )}
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4">
                      <div className="text-sm font-semibold mb-3">Recent Voice Memos ({voiceRecordings.length})</div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {voiceRecordings.length === 0 ? (
                          <div className="text-center text-gray-500 py-4">No recordings yet</div>
                        ) : (
                          voiceRecordings.map((recording) => (
                            <div key={recording.id} className="flex items-center justify-between p-2 bg-gray-800 rounded hover:bg-gray-700">
                              <div className="flex items-center gap-3">
                                <button onClick={() => playRecording(recording)} className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center text-sm">▶</button>
                                <div>
                                  <div className="text-sm font-semibold">{recording.name}</div>
                                  <div className="text-xs text-gray-400">{recording.duration} • {recording.date}</div>
                                </div>
                              </div>
                              <button onClick={() => deleteRecording(recording.id)} className="text-gray-400 hover:text-red-400 p-1">
                                <X size={16} />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Alarm Modal */}
                {activeModal === 'alarm' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-6">
                      <div className="text-center mb-6">
                        <svg className="w-16 h-16 text-yellow-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <circle cx="12" cy="13" r="7"/>
                          <polyline points="12 9 12 13 15 15"/>
                          <path d="M5 3 L2 6"/>
                          <path d="M19 3 L22 6"/>
                        </svg>
                        <div className="text-2xl font-bold mb-4">Alarms & Reminders</div>
                        
                        {/* Add New Alarm */}
                        <div className="flex gap-2 mb-4">
                          <input 
                            type="time" 
                            value={newAlarmTime}
                            onChange={(e) => setNewAlarmTime(e.target.value)}
                            className="flex-1 bg-gray-800 text-white rounded-lg px-3 py-2 outline-none"
                          />
                          <input 
                            type="text" 
                            value={newAlarmLabel}
                            onChange={(e) => setNewAlarmLabel(e.target.value)}
                            placeholder="Label (optional)"
                            className="flex-1 bg-gray-800 text-white rounded-lg px-3 py-2 outline-none"
                          />
                          <button onClick={addAlarm} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-semibold">
                            Add
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        <div className="text-sm text-gray-400 mb-2">Active Alarms ({alarms.length})</div>
                        {alarms.map((alarm) => (
                          <div key={alarm.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                            <div>
                              <div className="font-bold text-lg">{alarm.time}</div>
                              <div className="text-xs text-gray-400">{alarm.label}</div>
                            </div>
                            <div className="flex items-center gap-3">
                              <button 
                                onClick={() => toggleAlarm(alarm.id)}
                                className={`w-12 h-6 rounded-full transition-colors ${alarm.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                              >
                                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${alarm.enabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                              </button>
                              <button onClick={() => deleteAlarm(alarm.id)} className="text-gray-400 hover:text-red-400">
                                <X size={18} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes Modal */}
                {activeModal === 'notes' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-6">
                      <textarea 
                        value={currentNote}
                        onChange={(e) => setCurrentNote(e.target.value)}
                        className="w-full h-48 bg-gray-800 text-white rounded-lg p-4 outline-none resize-none"
                        placeholder="Start typing your notes..."
                      ></textarea>
                      <div className="flex justify-between items-center mt-4">
                        <div className="text-xs text-gray-400">{currentNote.length} characters</div>
                        <div className="flex gap-2">
                          <button onClick={() => setCurrentNote('')} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold">
                            Clear
                          </button>
                          <button onClick={saveNote} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold">
                            Save Note
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4">
                      <div className="text-sm font-semibold mb-3">Saved Notes ({savedNotes.length})</div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {savedNotes.length === 0 ? (
                          <div className="text-center text-gray-500 py-4">No saved notes yet</div>
                        ) : (
                          savedNotes.map((note) => (
                            <div key={note.id} className="flex items-center justify-between p-3 bg-gray-800 rounded hover:bg-gray-700">
                              <div onClick={() => loadNote(note)} className="flex-1 cursor-pointer">
                                <div className="font-semibold text-sm mb-1 truncate">{note.title}</div>
                                <div className="text-xs text-gray-400">{new Date(note.updatedAt).toLocaleDateString()}</div>
                              </div>
                              <button onClick={() => deleteNote(note.id)} className="text-gray-400 hover:text-red-400 p-1 ml-2">
                                <X size={16} />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Generic content for other modals */}
                {(activeModal === 'lease' || activeModal === 'pending' || activeModal === 'revenue' || 
                  activeModal === 'occupancy' || activeModal === 'market' || activeModal === 'status') && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-4">
                      <p className="text-gray-300">
                        Detailed information for this section is being prepared. This modal demonstrates
                        the interactive nature of the dashboard where every metric and card can be clicked
                        to reveal deeper insights and actionable data.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-2">Key Metric 1</div>
                        <div className="text-2xl font-bold">85%</div>
                      </div>
                      <div className="bg-green-500/10 border border-green-500 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-2">Key Metric 2</div>
                        <div className="text-2xl font-bold">$4.2M</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Task Modal */}
                {activeModal === 'task' && selectedData && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-6 h-6 rounded border-2 border-blue-400 cursor-pointer hover:bg-blue-400/20"></div>
                        <h3 className="text-xl font-bold">{selectedData.title}</h3>
                      </div>
                      
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`text-xs px-3 py-1 rounded font-semibold uppercase ${
                          selectedData.priority === 'Urgent' ? 'bg-red-500/20 text-red-400' :
                          selectedData.priority === 'High' ? 'bg-red-500/20 text-red-400' :
                          selectedData.priority === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-blue-500/20 text-white'
                        }`}>
                          {selectedData.priority}
                        </span>
                        <span className="text-sm text-gray-400">Due: {selectedData.due}</span>
                      </div>

                      <div className="mb-6">
                        <div className="text-sm font-semibold text-gray-400 mb-2">Description</div>
                        <p className="text-gray-300">{selectedData.description}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="text-sm text-gray-400 mb-1">Status</div>
                          <div className="text-base font-semibold">In Progress</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="text-sm text-gray-400 mb-1">Assigned To</div>
                          <div className="text-base font-semibold">Darren Woodson</div>
                        </div>
                      </div>

                      <div className="mt-6 flex gap-3">
                        <button className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-semibold">
                          Mark Complete
                        </button>
                        <button className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg font-semibold">
                          Edit Task
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Unread Messages Modal */}
                {activeModal === 'unread-messages' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="p-4 bg-gray-800 rounded-lg hover:bg-gray-750 cursor-pointer border-l-4 border-blue-400">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <img 
                                src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" 
                                alt="Sarah Chen"
                                className="w-10 h-10 rounded-full object-cover"
                              />
                              <div>
                                <div className="font-semibold">Sarah Chen</div>
                                <div className="text-xs text-gray-400">sarah.chen@realestate.com</div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-400">2:15 PM</div>
                          </div>
                          <div className="text-sm font-semibold mb-1">Contract Review - Harbor View</div>
                          <div className="text-sm text-gray-300">Urgent: Need your review on the lease renewal contract for Harbor View property...</div>
                        </div>

                        <div className="p-4 bg-gray-800 rounded-lg hover:bg-gray-750 cursor-pointer border-l-4 border-green-400">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <img 
                                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" 
                                alt="Michael Torres"
                                className="w-10 h-10 rounded-full object-cover"
                              />
                              <div>
                                <div className="font-semibold">Michael Torres</div>
                                <div className="text-xs text-gray-400">m.torres@realestate.com</div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-400">1:45 PM</div>
                          </div>
                          <div className="text-sm font-semibold mb-1">Downtown Plaza Financials</div>
                          <div className="text-sm text-gray-300">Q4 financial reports are ready for your review. Revenue exceeded projections by 12%...</div>
                        </div>

                        <div className="p-4 bg-gray-800 rounded-lg hover:bg-gray-750 cursor-pointer border-l-4 border-yellow-400">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <img 
                                src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop" 
                                alt="Amanda Park"
                                className="w-10 h-10 rounded-full object-cover"
                              />
                              <div>
                                <div className="font-semibold">Amanda Park</div>
                                <div className="text-xs text-gray-400">a.park@realestate.com</div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-400">11:30 AM</div>
                          </div>
                          <div className="text-sm font-semibold mb-1">Site Inspection Report</div>
                          <div className="text-sm text-gray-300">Oak Street property inspection completed. Found minor maintenance issues...</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Today's Meetings Modal */}
                {activeModal === 'todays-meetings' && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="p-4 bg-gray-800 rounded-lg border-l-4 border-red-400">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-lg font-bold">Lease Negotiation - Harbor View</div>
                            <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400 font-semibold">URGENT</span>
                          </div>
                          <div className="text-sm text-gray-400 mb-2">2:30 PM - 3:30 PM</div>
                          <div className="text-sm text-gray-300 mb-3">Discuss lease renewal terms with Harbor View management</div>
                          <div className="flex items-center gap-2">
                            <img 
                              src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" 
                              alt="Attendee"
                              className="w-8 h-8 rounded-full object-cover"
                            />
                            <span className="text-xs text-gray-400">Sarah Chen + 3 others</span>
                          </div>
                        </div>

                        <div className="p-4 bg-gray-800 rounded-lg border-l-4 border-blue-400">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-lg font-bold">Budget Review Meeting</div>
                            <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-white font-semibold">SCHEDULED</span>
                          </div>
                          <div className="text-sm text-gray-400 mb-2">4:00 PM - 5:00 PM</div>
                          <div className="text-sm text-gray-300 mb-3">Review and approve $850K renovation budget for downtown property</div>
                          <div className="flex items-center gap-2">
                            <img 
                              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" 
                              alt="Attendee"
                              className="w-8 h-8 rounded-full object-cover"
                            />
                            <span className="text-xs text-gray-400">Michael Torres + 2 others</span>
                          </div>
                        </div>

                        <div className="p-4 bg-gray-800 rounded-lg border-l-4 border-green-400">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-lg font-bold">Team Standup</div>
                            <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400 font-semibold">COMPLETED</span>
                          </div>
                          <div className="text-sm text-gray-400 mb-2">9:00 AM - 9:30 AM</div>
                          <div className="text-sm text-gray-300 mb-3">Daily team sync and status updates</div>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs">
                              +5
                            </div>
                            <span className="text-xs text-gray-400">Full team</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {(activeModal === 'lease' || activeModal === 'pending' || activeModal === 'revenue' || 
                  activeModal === 'occupancy' || activeModal === 'market' || activeModal === 'status') && (
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-lg p-4">
                      <p className="text-gray-300">
                        Detailed information for this section is being prepared. This modal demonstrates
                        the interactive nature of the dashboard where every metric and card can be clicked
                        to reveal deeper insights and actionable data.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-2">Key Metric 1</div>
                        <div className="text-2xl font-bold">85%</div>
                      </div>
                      <div className="bg-green-500/10 border border-green-500 rounded-lg p-4">
                        <div className="text-sm text-gray-400 mb-2">Key Metric 2</div>
                        <div className="text-2xl font-bold">$4.2M</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Document Preview Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setSelectedDocument(null)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-3xl max-h-[90vh] mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-xl ${getFileIcon(selectedDocument.file_type).color}`}>
                  {getFileIcon(selectedDocument.file_type).icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{selectedDocument.name || selectedDocument.original_filename}</h3>
                  <p className="text-xs text-gray-400">{selectedDocument.size} • {selectedDocument.file_type || 'Document'}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedDocument(null)}
                className="p-2 hover:bg-gray-900 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Document Preview */}
              <div className="bg-gray-900 rounded-lg p-4 mb-4">
                {selectedDocument.file_type?.toLowerCase().includes('pdf') || selectedDocument.file_type?.toLowerCase().includes('image') || selectedDocument.file_type?.toLowerCase().includes('png') || selectedDocument.file_type?.toLowerCase().includes('jpg') ? (
                  <div className="aspect-[4/3] bg-gray-800 rounded-lg flex items-center justify-center">
                    {selectedDocument.file_path ? (
                      <iframe 
                        src={`${API_URL}${selectedDocument.file_path}`} 
                        className="w-full h-full rounded-lg"
                        title={selectedDocument.name}
                      />
                    ) : (
                      <div className="text-center">
                        <FileText size={48} className="mx-auto text-gray-600 mb-3" />
                        <p className="text-gray-400">Preview not available</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-gray-800 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className={`text-6xl mb-4 ${getFileIcon(selectedDocument.file_type).color}`}>
                        {getFileIcon(selectedDocument.file_type).icon}
                      </div>
                      <p className="text-gray-400 mb-4">{selectedDocument.file_type || 'Document'} file</p>
                      {selectedDocument.file_path && (
                        <a 
                          href={`${API_URL}${selectedDocument.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-2"
                        >
                          <Eye size={16} />
                          Open Document
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Document Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Category</p>
                  <p className="text-sm text-white font-medium">{selectedDocument.category || 'Uncategorized'}</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Created</p>
                  <p className="text-sm text-white font-medium">
                    {selectedDocument.created_at ? new Date(selectedDocument.created_at).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-400/20 flex items-center justify-end gap-3">
              <button 
                onClick={() => navigate('/documents-re')}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium"
              >
                Go to Documents
              </button>
              {selectedDocument.file_path && (
                <a 
                  href={`${API_URL}${selectedDocument.file_path}`}
                  download
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Meeting Details Modal */}
      {selectedDashboardMeeting && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setSelectedDashboardMeeting(null)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-lg mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${getMeetingTypeColor(selectedDashboardMeeting.category || selectedDashboardMeeting.type || 'Personal')} flex items-center justify-center text-white font-bold`}>
                  {(selectedDashboardMeeting.title || selectedDashboardMeeting.name || 'M')[0].toUpperCase()}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{selectedDashboardMeeting.title || selectedDashboardMeeting.name || 'Meeting'}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded ${getMeetingTypeColor(selectedDashboardMeeting.category || selectedDashboardMeeting.type || 'Personal')} text-white`}>
                    {selectedDashboardMeeting.category || selectedDashboardMeeting.type || 'Personal'}
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedDashboardMeeting(null)}
                className="p-2 hover:bg-gray-900 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Date & Time */}
              <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg">
                <Calendar size={20} className="text-white mt-0.5" />
                <div>
                  <p className="text-sm text-gray-400">Date & Time</p>
                  <p className="text-white font-medium">
                    {selectedDashboardMeeting.date && selectedDashboardMeeting.date !== 'TBD' 
                      ? new Date(String(selectedDashboardMeeting.date).split(' ')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                      : 'TBD'}
                  </p>
                  <p className="text-sm text-gray-400">{selectedDashboardMeeting.time || 'Time TBD'}</p>
                </div>
              </div>
              
              {/* Attendee */}
              {selectedDashboardMeeting.participants?.[0] && (
                <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg">
                  <Users size={20} className="text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">Attendee</p>
                    <p className="text-white font-medium">{selectedDashboardMeeting.participants[0].name || selectedDashboardMeeting.participants[0]}</p>
                    {selectedDashboardMeeting.participants[0].company && (
                      <p className="text-sm text-gray-400">{selectedDashboardMeeting.participants[0].company}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Location */}
              {selectedDashboardMeeting.location && (
                <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg">
                  <MapPin size={20} className="text-green-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">Location</p>
                    <p className="text-white font-medium">{selectedDashboardMeeting.location}</p>
                  </div>
                </div>
              )}
              
              {/* Agenda/Notes */}
              {(selectedDashboardMeeting.agenda || selectedDashboardMeeting.notes) && (
                <div className="p-3 bg-gray-900/50 rounded-lg">
                  <p className="text-sm text-gray-400 mb-2">Agenda / Notes</p>
                  <p className="text-white text-sm">{selectedDashboardMeeting.agenda || selectedDashboardMeeting.notes}</p>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-400/20 flex items-center justify-end gap-3">
              <button 
                onClick={() => {
                  setSelectedDashboardMeeting(null);
                  navigate('/meetings-re');
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                Go to Meetings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Details Modal */}
      {selectedDashboardContact && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setSelectedDashboardContact(null)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-lg mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full ${getCategoryColor(selectedDashboardContact.category)} flex items-center justify-center text-white font-bold text-lg`}>
                  {getInitials(selectedDashboardContact.name)}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{selectedDashboardContact.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded ${getCategoryColor(selectedDashboardContact.category)} text-white`}>
                    {selectedDashboardContact.category || 'Contact'}
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedDashboardContact(null)}
                className="p-2 hover:bg-gray-900 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Company */}
              {selectedDashboardContact.company && (
                <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg">
                  <Building2 size={20} className="text-white mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">Company</p>
                    <p className="text-white font-medium">{selectedDashboardContact.company}</p>
                  </div>
                </div>
              )}
              
              {/* Email */}
              {selectedDashboardContact.email && (
                <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg">
                  <Mail size={20} className="text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">Email</p>
                    <a href={`mailto:${selectedDashboardContact.email}`} className="text-white hover:text-white font-medium">
                      {selectedDashboardContact.email}
                    </a>
                  </div>
                </div>
              )}
              
              {/* Phone */}
              {selectedDashboardContact.phone && (
                <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg">
                  <Phone size={20} className="text-green-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">Phone</p>
                    <a href={`tel:${selectedDashboardContact.phone}`} className="text-white hover:text-white font-medium">
                      {selectedDashboardContact.phone}
                    </a>
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {selectedDashboardContact.notes && (
                <div className="p-3 bg-gray-900/50 rounded-lg">
                  <p className="text-sm text-gray-400 mb-2">Notes</p>
                  <p className="text-white text-sm">{selectedDashboardContact.notes}</p>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-400/20 flex items-center justify-between">
              <div className="flex gap-2">
                {selectedDashboardContact.phone && (
                  <a 
                    href={`tel:${selectedDashboardContact.phone}`}
                    className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    title="Call"
                  >
                    <Phone size={18} />
                  </a>
                )}
                {selectedDashboardContact.email && (
                  <a 
                    href={`mailto:${selectedDashboardContact.email}`}
                    className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    title="Email"
                  >
                    <Mail size={18} />
                  </a>
                )}
              </div>
              <button 
                onClick={() => {
                  setSelectedDashboardContact(null);
                  navigate('/contacts-re');
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                Go to Contacts
              </button>
            </div>
          </div>
        </div>
      )}

      </div>

      {/* Bottom Music Player Bar */}
      {showMusicPlayer && (
        <div className="fixed bottom-0 left-0 right-0 bg-black z-50 py-3 px-6">
          <div className="max-w-screen-xl mx-auto flex items-center gap-6">
            {/* Album Cover */}
            <img 
              src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=200&h=200&fit=crop" 
              alt="Album Cover"
              className="w-14 h-14 rounded-lg object-cover shadow-lg"
            />
            
            {/* Song Info */}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-base truncate">Midnight City</div>
              <div className="text-sm text-gray-400 truncate">M83 • Hurry Up, We're Dreaming</div>
            </div>

            {/* Play Controls */}
            <div className="flex items-center gap-4">
              <button className="hover:text-purple-400 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                </svg>
              </button>
              <button 
                className="w-12 h-12 bg-purple-500 hover:bg-purple-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
                onClick={() => setIsPlaying(!isPlaying)}
              >
                {isPlaying ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>
              <button className="hover:text-purple-400 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 18h2V6h-2zm-3.5-6L4 6v12z"/>
                </svg>
              </button>
            </div>

            {/* Progress Bar */}
            <div className="flex items-center gap-3 flex-1 max-w-md">
              <span className="text-xs text-gray-400">1:23</span>
              <div className="flex-1 bg-gray-700 rounded-full h-1.5 cursor-pointer">
                <div className="bg-purple-400 rounded-full h-1.5" style={{ width: '35%' }}></div>
              </div>
              <span className="text-xs text-gray-400">3:58</span>
            </div>

            {/* Volume Control */}
            <div className="flex items-center gap-3 w-32">
              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
              </svg>
              <div className="flex-1 bg-gray-700 rounded-full h-1.5">
                <div className="bg-white rounded-full h-1.5" style={{ width: `${volume}%` }}></div>
              </div>
            </div>

            {/* Close Button */}
            <button 
              className="hover:text-red-400 transition-colors"
              onClick={() => {
                setShowMusicPlayer(false);
                setIsPlaying(false);
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4" onClick={() => setShowProfileModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Edit Profile</h2>
              <button 
                onClick={() => setShowProfileModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Large Profile Image Preview */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative group mb-4">
                <img 
                  src={tempProfileImage} 
                  alt="Profile Preview"
                  className="w-32 h-32 rounded-full object-cover border-4 border-cyan-500 shadow-lg"
                />
                <button 
                  onClick={() => profileFileInputRef.current?.click()}
                  className="absolute inset-0 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <div className="text-center">
                    <svg className="w-8 h-8 text-white mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-xs text-white">Change Photo</span>
                  </div>
                </button>
              </div>
              <input 
                type="file" 
                ref={profileFileInputRef}
                accept="image/*"
                onChange={handleProfileImageUpload}
                className="hidden"
              />
              <button 
                onClick={() => profileFileInputRef.current?.click()}
                className="text-white hover:text-white text-sm font-medium"
              >
                Upload New Image
              </button>
            </div>

            {/* Profile Name */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Name</label>
              <input 
                type="text"
                value={tempProfileName}
                onChange={(e) => setTempProfileName(e.target.value)}
                placeholder="Enter your name"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                data-testid="profile-name-input"
              />
              <p className="text-xs text-gray-500 mt-1">This name will appear in your daily brief greeting</p>
            </div>

            {/* Role Title */}
            <div className="mb-6">
              <label className="block text-sm text-gray-400 mb-2">Role Title</label>
              <input 
                type="text"
                value={tempProfileRole}
                onChange={(e) => setTempProfileRole(e.target.value)}
                placeholder="Enter your role title"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button 
                onClick={() => setShowProfileModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={saveProfileChanges}
                className="flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-600 rounded-lg font-medium transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateManagingPartnerDashboardNew;
